import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { X } from 'lucide-react'
import axios from '../../config/axios'
import InfiniteSelect from '../InfiniteSelect'
import PantoneSelect, { PantoneValue } from '../PantoneSelect'
import { WriteRegistrationType, integrationModules, ModuleField } from './Modals/integrationModulesConfig'

type IdValue = number | string

interface OptionItem {
  id: IdValue
  name: string
}

interface GradeOption extends OptionItem {
  typeName: string
}

interface ColorOption extends OptionItem {
  pantone?: PantoneValue | null
  hex?: string
}

interface SelectedColor extends ColorOption {
  pantone: PantoneValue | null
}

interface CollectionAviamentoMaterialOption extends OptionItem {
  id: string
  categoryId: number
  categoryName: string
  materialId: number
  materialName: string
}

interface ProductCompositionDraft {
  materialId: number | null
  usage: string
  unitOfMeasurementId: number | null
  collectionColorId: number | null
}

interface WriteRegistrationDraftOption {
  id: string
  label: string
}

interface IntegratedWriteCreateModalProps {
  isOpen: boolean
  moduleType?: WriteRegistrationType
  requiredFieldKeys?: string[]
  optionalFieldKeys?: string[]
  draftOptions?: WriteRegistrationDraftOption[]
  activeDraftId?: string | null
  onCreateDraft?: () => void
  onSelectDraft?: (draftId: string) => void
  stageDraft?: Record<string, any>
  onStageDraftChange?: (draft: Record<string, any>) => void
  autoCreateWhenReady?: boolean
  autoCreateCreatedRecord?: {
    id: number | string
    name: string
    viewUrl: string | null
  } | null
  onClose: () => void
  onCreated: (option: any) => void
  inline?: boolean
}

const writeRegistrationSourceModuleId: Record<WriteRegistrationType, string> = {
  collection: 'fashion-collections',
  family: 'fashion-families',
  product: 'fashion-products',
  fabric: 'fashion-stock',
  trim: 'resources',
}

interface RegistrationOptionalFieldConfig extends ModuleField {
  rendererType?:
    | 'default'
    | 'collection-groups'
    | 'collection-themes'
    | 'family-groups'
    | 'family-themes'
    | 'product-theme'
    | 'fabric-measurement-unit'
    | 'trim-stock-unit'
    | 'trim-type'
    | 'trim-stock-street'
    | 'trim-suppliers'
    | 'trim-prices'
}

const gatewayBaseUrl = process.env.REACT_APP_GATEWAY_BASE_URL || ''

const parseArrayResponse = (raw: any, preferredKey?: string): any[] => {
  if (Array.isArray(raw)) return raw
  if (preferredKey && Array.isArray(raw?.[preferredKey])) return raw[preferredKey]
  if (Array.isArray(raw?.data)) return raw.data
  if (Array.isArray(raw?.content)) return raw.content
  if (Array.isArray(raw?.items)) return raw.items
  if (Array.isArray(raw?.results)) return raw.results
  const firstArrayKey = raw && typeof raw === 'object'
    ? Object.keys(raw).find((key) => Array.isArray(raw[key]))
    : undefined
  if (firstArrayKey) return raw[firstArrayKey]
  return []
}

const normalizeText = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()

const toOption = (item: any): OptionItem => {
  const numericId = Number(item?.id)
  const id = Number.isFinite(numericId) ? numericId : String(item?.id || '')
  const name = String(item?.name || item?.code || item?.description || item?.productCode || item?.material || item?.type || item?.label || id)
  return { id, name }
}

const toColorOption = (item: any): ColorOption => {
  const base = toOption(item)
  const pantoneRaw = item?.Pantone || item?.pantone || null
  const pantone = pantoneRaw?.code
    ? {
      code: String(pantoneRaw.code),
      name: pantoneRaw.name ? String(pantoneRaw.name) : undefined,
      hex: pantoneRaw.hex ? String(pantoneRaw.hex) : undefined,
    }
    : null

  return {
    ...base,
    pantone,
    hex: pantone?.hex,
  }
}

const toCollectionMaterialOption = (item: any): OptionItem | null => {
  const collectionMaterialId = Number(item?.id || item?.collectionMaterialId || item?.collection_material_id)
  if (!Number.isFinite(collectionMaterialId)) return null

  const baseMaterialName = String(
    item?.Material?.name ||
    item?.material?.name ||
    item?.materialName ||
    item?.name ||
    ''
  ).trim()

  const links = Array.isArray(item?.CollectionMaterialResources)
    ? item.CollectionMaterialResources
    : []

  const resourceLabels = links
    .map((link: any) => {
      const categoryName = String(link?.Category_Resource?.name || link?.categoryName || '').trim()
      const materialName = String(link?.Material?.name || link?.materialName || '').trim()
      if (categoryName && materialName) return `${categoryName}: ${materialName}`
      if (materialName) return materialName
      if (categoryName) return categoryName
      return ''
    })
    .filter((value: string) => value)

  const uniqueResourceLabels = Array.from(new Set(resourceLabels))
  const materialName = baseMaterialName || `Conjunto ${collectionMaterialId}`
  const suffix = uniqueResourceLabels.length > 0
    ? ` - ${uniqueResourceLabels.slice(0, 2).join(' | ')}${uniqueResourceLabels.length > 2 ? '...' : ''}`
    : ''

  return {
    id: collectionMaterialId,
    name: `${materialName}${suffix}`,
  }
}

const createLocalFetch = <T extends OptionItem>(options: T[]) => {
  return async (limit: number, offset: number, search?: string): Promise<T[]> => {
    const normalizedSearch = normalizeText(search || '')
    const filtered = normalizedSearch
      ? options.filter((option) => normalizeText(String(option.name || '')).includes(normalizedSearch))
      : options
    return filtered.slice(offset, offset + limit)
  }
}

const measurementUnitChoices: OptionItem[] = [
  { id: 'KG', name: 'KG' },
  { id: 'MM', name: 'MM' },
  { id: 'UN', name: 'UN' },
]

const IntegratedWriteCreateModal = ({
  isOpen,
  moduleType,
  requiredFieldKeys = [],
  optionalFieldKeys = [],
  draftOptions = [],
  activeDraftId = null,
  onCreateDraft,
  onSelectDraft,
  stageDraft,
  onStageDraftChange,
  autoCreateWhenReady = false,
  autoCreateCreatedRecord = null,
  onClose,
  onCreated,
  inline = false,
}: IntegratedWriteCreateModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingOptions, setIsLoadingOptions] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [gradeOptions, setGradeOptions] = useState<GradeOption[]>([])
  const [materialOptions, setMaterialOptions] = useState<OptionItem[]>([])
  const [resourceCategoryOptions, setResourceCategoryOptions] = useState<OptionItem[]>([])
  const [collectionOptions, setCollectionOptions] = useState<OptionItem[]>([])
  const [familyOptions, setFamilyOptions] = useState<OptionItem[]>([])
  const [groupOptions, setGroupOptions] = useState<OptionItem[]>([])
  const [unitOptions, setUnitOptions] = useState<OptionItem[]>([])
  const [collectionColorOptions, setCollectionColorOptions] = useState<ColorOption[]>([])
  const [collectionMaterialOptions, setCollectionMaterialOptions] = useState<OptionItem[]>([])
  const [fabricColorOptions, setFabricColorOptions] = useState<ColorOption[]>([])
  const [trimColorOptions, setTrimColorOptions] = useState<ColorOption[]>([])
  const [trimTypeOptions, setTrimTypeOptions] = useState<OptionItem[]>([])
  const [trimStockStreetOptions, setTrimStockStreetOptions] = useState<OptionItem[]>([])
  const [trimSupplierOptions, setTrimSupplierOptions] = useState<OptionItem[]>([])
  const [trimPriceUnitOptions, setTrimPriceUnitOptions] = useState<OptionItem[]>([])
  const [selectedTrimSupplierIds, setSelectedTrimSupplierIds] = useState<number[]>([])
  const [trimPriceValue, setTrimPriceValue] = useState('')
  const [trimPriceUnitId, setTrimPriceUnitId] = useState<number | null>(null)

  const [collectionName, setCollectionName] = useState('')
  const [collectionSelectedColors, setCollectionSelectedColors] = useState<SelectedColor[]>([])
  const [activeCollectionColorId, setActiveCollectionColorId] = useState<IdValue | null>(null)
  const [collectionGradeIds, setCollectionGradeIds] = useState<number[]>([])
  const [collectionMaterialIds, setCollectionMaterialIds] = useState<number[]>([])
  const [collectionAviamentoOptions, setCollectionAviamentoOptions] = useState<CollectionAviamentoMaterialOption[]>([])
  const [collectionAviamentoSelections, setCollectionAviamentoSelections] = useState<CollectionAviamentoMaterialOption[]>([])
  const [collectionThemeOptions, setCollectionThemeOptions] = useState<OptionItem[]>([])
  const [selectedCollectionGroupIds, setSelectedCollectionGroupIds] = useState<number[]>([])
  const [selectedCollectionThemeIds, setSelectedCollectionThemeIds] = useState<number[]>([])

  const [familyName, setFamilyName] = useState('')
  const [familyCollectionId, setFamilyCollectionId] = useState<number | null>(null)
  const [familyGradeIds, setFamilyGradeIds] = useState<number[]>([])
  const [familyColorIds, setFamilyColorIds] = useState<number[]>([])
  const [familyCollectionMaterialIds, setFamilyCollectionMaterialIds] = useState<number[]>([])
  const [familyGroupOptions, setFamilyGroupOptions] = useState<OptionItem[]>([])
  const [familyThemeOptions, setFamilyThemeOptions] = useState<OptionItem[]>([])
  const [selectedFamilyGroupIds, setSelectedFamilyGroupIds] = useState<number[]>([])
  const [selectedFamilyThemeIds, setSelectedFamilyThemeIds] = useState<number[]>([])

  const [productCode, setProductCode] = useState('')
  const [productName, setProductName] = useState('')
  const [productCollectionId, setProductCollectionId] = useState<number | null>(null)
  const [productFamilyId, setProductFamilyId] = useState<number | null>(null)
  const [productGroupId, setProductGroupId] = useState<number | null>(null)
  const [productThemeOptions, setProductThemeOptions] = useState<OptionItem[]>([])
  const [selectedProductThemeId, setSelectedProductThemeId] = useState<number | null>(null)
  const [productGradeOptions, setProductGradeOptions] = useState<GradeOption[]>([])
  const [selectedProductGradeIds, setSelectedProductGradeIds] = useState<number[]>([])
  const [productCompositions, setProductCompositions] = useState<ProductCompositionDraft[]>([
    { materialId: null, usage: '', unitOfMeasurementId: null, collectionColorId: null },
  ])

  const [fabricCode, setFabricCode] = useState('')
  const [fabricMaterialId, setFabricMaterialId] = useState<number | null>(null)
  const [fabricItemColorId, setFabricItemColorId] = useState<number | null>(null)
  const [fabricColor, setFabricColor] = useState('')
  const [fabricPantone, setFabricPantone] = useState<PantoneValue | null>(null)

  const [trimName, setTrimName] = useState('')
  const [trimCategoryId, setTrimCategoryId] = useState<number | null>(null)
  const [trimMaterialId, setTrimMaterialId] = useState<number | null>(null)
  const [trimItemColorId, setTrimItemColorId] = useState<number | null>(null)
  const [trimColor, setTrimColor] = useState('')
  const [trimPantone, setTrimPantone] = useState<PantoneValue | null>(null)
  const [optionalFieldValues, setOptionalFieldValues] = useState<Record<string, string>>({})

  const [enabledGradeTypes, setEnabledGradeTypes] = useState<Record<string, boolean>>({})
  const hasHydratedStageDraftRef = useRef(false)
  const hydratedStageDraftSessionRef = useRef<string>('')
  const lastEmittedStageDraftRef = useRef<string>('')
  const lastDraftSessionHandledForEmissionRef = useRef<string>('')

  useEffect(() => {
    if (!autoCreateWhenReady) return

    console.debug('[PIPELINE-WRITE-STAGED][FRONT][MODAL] props-snapshot', {
      moduleType,
      inline,
      isOpen,
      activeDraftId,
      draftOptionsCount: draftOptions.length,
      draftOptionIds: draftOptions.map((item) => item.id),
      stageDraftKeys: Object.keys(stageDraft || {}),
      hydratedStageDraftSessionRef: hydratedStageDraftSessionRef.current,
      hasHydratedStageDraftRef: hasHydratedStageDraftRef.current,
      lastDraftSessionHandledForEmissionRef: lastDraftSessionHandledForEmissionRef.current,
    })
  }, [activeDraftId, autoCreateWhenReady, draftOptions, inline, isOpen, moduleType, stageDraft])

  const writeRegistrationDefaultRequiredKeys = useMemo(() => ({
    collection: ['name', 'collectionColors', 'gradeIds', 'materials'],
    family: ['name', 'collectionId', 'collectionColorIds', 'gradeIds', 'materials'],
    product: ['productCode', 'name', 'collectionId', 'familyId', 'groupId', 'gradeIds', 'compositionItems'],
    fabric: ['code', 'material', 'color', 'pantoneCode'],
    trim: ['name', 'categoryId', 'materialId', 'colors'],
  }), [])

  const effectiveRequiredFieldKeys = useMemo(() => {
    if (!moduleType) return requiredFieldKeys
    if (requiredFieldKeys.length > 0) return requiredFieldKeys
    return writeRegistrationDefaultRequiredKeys[moduleType] || []
  }, [moduleType, requiredFieldKeys, writeRegistrationDefaultRequiredKeys])

  const hasMeaningfulStageDraftValue = useCallback((value: any): boolean => {
    if (value === null || value === undefined) return false
    if (typeof value === 'string') return value.trim() !== ''
    if (typeof value === 'number') return Number.isFinite(value)
    if (typeof value === 'boolean') return true
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'object') return Object.keys(value).length > 0
    return false
  }, [])

  const stageDraftHasFieldValue = useCallback((fieldKey: string): boolean => {
    const draft = stageDraft || {}
    const hasDirectValue = hasMeaningfulStageDraftValue((draft as any)[fieldKey])
    if (hasDirectValue) return true

    if (moduleType === 'collection' && fieldKey === 'materials') {
      return hasMeaningfulStageDraftValue((draft as any).materialIds) || hasMeaningfulStageDraftValue((draft as any).collectionAviamentoSelections)
    }

    if (moduleType === 'family' && fieldKey === 'materials') {
      return hasMeaningfulStageDraftValue((draft as any).materials)
    }

    if (moduleType === 'product' && fieldKey === 'compositionItems') {
      return hasMeaningfulStageDraftValue((draft as any).compositionItems) || hasMeaningfulStageDraftValue((draft as any).productItemColors)
    }

    if (moduleType === 'fabric' && fieldKey === 'material') {
      return hasMeaningfulStageDraftValue((draft as any).materialId) || hasMeaningfulStageDraftValue((draft as any).materialName)
    }

    if (moduleType === 'fabric' && fieldKey === 'color') {
      return hasMeaningfulStageDraftValue((draft as any).color) || hasMeaningfulStageDraftValue((draft as any).itemColorId)
    }

    if (moduleType === 'trim' && fieldKey === 'colors') {
      return hasMeaningfulStageDraftValue((draft as any).colors) || hasMeaningfulStageDraftValue((draft as any).itemColorId) || hasMeaningfulStageDraftValue((draft as any).color)
    }

    return false
  }, [hasMeaningfulStageDraftValue, moduleType, stageDraft])

  const shouldRenderRequiredField = useCallback((fieldKey: string) => {
    if (!autoCreateWhenReady) return true
    const isRequiredInCurrentStep = effectiveRequiredFieldKeys.length === 0 || effectiveRequiredFieldKeys.includes(fieldKey)
    const hasStageValueForField = stageDraftHasFieldValue(fieldKey)
    const shouldRender = isRequiredInCurrentStep || hasStageValueForField

    console.debug('[PIPELINE-WRITE-STAGED][FRONT][MODAL] required-field-visibility', {
      moduleType,
      fieldKey,
      isRequiredInCurrentStep,
      hasStageValueForField,
      shouldRender,
      stageDraftKeys: Object.keys(stageDraft || {}),
    })

    return shouldRender
  }, [autoCreateWhenReady, effectiveRequiredFieldKeys, moduleType, stageDraft, stageDraftHasFieldValue])

  const resolveOptionalRendererType = (registrationType: WriteRegistrationType, field: ModuleField): RegistrationOptionalFieldConfig['rendererType'] => {
    if (registrationType === 'fabric' && field.field === 'measurementUnit') return 'fabric-measurement-unit'
    if (registrationType === 'trim' && field.field === 'stockUnit') return 'trim-stock-unit'
    return 'default'
  }

  const optionalFieldConfigs = useMemo((): RegistrationOptionalFieldConfig[] => {
    if (!moduleType || optionalFieldKeys.length === 0) return []

    const sourceModuleId = writeRegistrationSourceModuleId[moduleType]
    const sourceModule = integrationModules.find((module) => module.id === sourceModuleId)
    const selectedKeys = new Set(optionalFieldKeys)

    const baseConfigs = (sourceModule?.fields || [])
      .filter((field) => selectedKeys.has(field.field))
      .map((field) => ({ ...field, rendererType: resolveOptionalRendererType(moduleType, field) }))

    const syntheticConfigs: RegistrationOptionalFieldConfig[] = []

    if (moduleType === 'collection' && selectedKeys.has('groupIds')) {
      syntheticConfigs.push({ field: 'groupIds', fieldName: 'Grupos', rendererType: 'collection-groups' })
    }

    if (moduleType === 'collection' && selectedKeys.has('themeIds')) {
      syntheticConfigs.push({ field: 'themeIds', fieldName: 'Temas', rendererType: 'collection-themes' })
    }

    if (moduleType === 'family' && selectedKeys.has('groupIds')) {
      syntheticConfigs.push({ field: 'groupIds', fieldName: 'Grupos', rendererType: 'family-groups' })
    }

    if (moduleType === 'family' && selectedKeys.has('themeIds')) {
      syntheticConfigs.push({ field: 'themeIds', fieldName: 'Temas', rendererType: 'family-themes' })
    }

    if (moduleType === 'product' && selectedKeys.has('themeId')) {
      syntheticConfigs.push({ field: 'themeId', fieldName: 'Tema', rendererType: 'product-theme' })
    }

    if (moduleType === 'trim' && selectedKeys.has('typeId')) {
      syntheticConfigs.push({ field: 'typeId', fieldName: 'Tipo', rendererType: 'trim-type' })
    }

    if (moduleType === 'trim' && selectedKeys.has('stockStreetId')) {
      syntheticConfigs.push({ field: 'stockStreetId', fieldName: 'Rua do estoque', rendererType: 'trim-stock-street' })
    }

    if (moduleType === 'trim' && selectedKeys.has('suppliers')) {
      syntheticConfigs.push({ field: 'suppliers', fieldName: 'Fornecedores', rendererType: 'trim-suppliers' })
    }

    if (moduleType === 'trim' && selectedKeys.has('prices')) {
      syntheticConfigs.push({ field: 'prices', fieldName: 'Preços', rendererType: 'trim-prices' })
    }

    const mergedByField = new Map<string, RegistrationOptionalFieldConfig>()
    baseConfigs.forEach((fieldConfig) => mergedByField.set(fieldConfig.field, fieldConfig))
    syntheticConfigs.forEach((fieldConfig) => mergedByField.set(fieldConfig.field, fieldConfig))

    return Array.from(mergedByField.values())
  }, [moduleType, optionalFieldKeys])

  const optionalFieldConfigsByGrid = useMemo(() => {
    return optionalFieldConfigs.filter((field) => {
      if (field.rendererType && field.rendererType !== 'default') return true
      return !field.field.includes('.') && !field.filterEmpty
    })
  }, [optionalFieldConfigs])

  const buildOptionalFieldsPayload = () => {
    const payload: Record<string, any> = {}

    optionalFieldConfigsByGrid.forEach((fieldConfig) => {
      if (fieldConfig.rendererType === 'collection-groups') {
        if (selectedCollectionGroupIds.length > 0) {
          payload.groupIds = selectedCollectionGroupIds
        }
        return
      }

      if (fieldConfig.rendererType === 'collection-themes') {
        if (selectedCollectionThemeIds.length > 0) {
          payload.themeIds = selectedCollectionThemeIds
        }
        return
      }

      if (fieldConfig.rendererType === 'family-groups') {
        if (selectedFamilyGroupIds.length > 0) {
          payload.groupIds = selectedFamilyGroupIds
        }
        return
      }

      if (fieldConfig.rendererType === 'family-themes') {
        if (selectedFamilyThemeIds.length > 0) {
          payload.themeIds = selectedFamilyThemeIds
        }
        return
      }

      if (fieldConfig.rendererType === 'product-theme') {
        if (selectedProductThemeId) {
          payload.themeId = selectedProductThemeId
        }
        return
      }

      if (fieldConfig.rendererType === 'fabric-measurement-unit') {
        if (String(optionalFieldValues[fieldConfig.field] || '').trim() !== '') {
          payload.measurementUnit = String(optionalFieldValues[fieldConfig.field]).trim().toUpperCase()
        }
        return
      }

      if (fieldConfig.rendererType === 'trim-stock-unit') {
        if (String(optionalFieldValues[fieldConfig.field] || '').trim() !== '') {
          payload.stockUnit = String(optionalFieldValues[fieldConfig.field]).trim().toUpperCase()
        }
        return
      }

      if (fieldConfig.rendererType === 'trim-type') {
        const parsedTypeId = Number(optionalFieldValues[fieldConfig.field])
        if (Number.isFinite(parsedTypeId)) {
          payload.typeId = parsedTypeId
        }
        return
      }

      if (fieldConfig.rendererType === 'trim-stock-street') {
        const parsedStreetId = Number(optionalFieldValues[fieldConfig.field])
        if (Number.isFinite(parsedStreetId)) {
          payload.stockStreetId = parsedStreetId
        }
        return
      }

      if (fieldConfig.rendererType === 'trim-suppliers') {
        if (selectedTrimSupplierIds.length > 0) {
          payload.suppliers = selectedTrimSupplierIds
        }
        return
      }

      if (fieldConfig.rendererType === 'trim-prices') {
        const parsedPrice = Number(trimPriceValue)
        if (Number.isFinite(parsedPrice) && trimPriceUnitId) {
          payload.prices = [
            {
              price: parsedPrice,
              unitOfMeasurementId: trimPriceUnitId,
            },
          ]
        }
        return
      }

      const rawValue = optionalFieldValues[fieldConfig.field]
      if (rawValue === undefined || rawValue === null) return
      if (String(rawValue).trim() === '') return

      if (fieldConfig.valueType === 'boolean') {
        payload[fieldConfig.field] = rawValue === 'true'
        return
      }

      if (fieldConfig.valueType === 'number') {
        const parsedNumber = Number(rawValue)
        if (Number.isFinite(parsedNumber)) {
          payload[fieldConfig.field] = parsedNumber
        }
        return
      }

      payload[fieldConfig.field] = rawValue
    })

    return payload
  }

  const fetchOptions = async (url: string, preferredKey?: string, params?: Record<string, any>) => {
    const response = await axios.get(`${gatewayBaseUrl}${url}`, { params })
    const rows = parseArrayResponse(response.data, preferredKey)
    return rows.map(toOption)
  }

  const fetchColorOptions = async (url: string, preferredKey?: string, params?: Record<string, any>) => {
    const response = await axios.get(`${gatewayBaseUrl}${url}`, { params })
    const rows = parseArrayResponse(response.data, preferredKey)
    return rows.map(toColorOption)
  }

  const fetchCompanyOptions = async () => {
    const response = await axios.get(`${gatewayBaseUrl}/companies/companies`, {
      params: { limit: 200, offset: 0 },
    })
    const raw = response.data?.data ?? response.data
    const rows = parseArrayResponse(raw, 'content')
    const normalized: OptionItem[] = []
    rows.forEach((item: any) => {
      const numericId = Number(item?.id)
      if (!Number.isFinite(numericId)) return
      const name = String(item?.companyName || item?.name || '').trim()
      if (!name) return
      normalized.push({ id: numericId, name })
    })
    return normalized
  }

  const fetchGradeOptions = async () => {
    const response = await axios.get(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/grades`)
    const rows = parseArrayResponse(response.data)

    const grades: GradeOption[] = rows.map((row) => {
      const option = toOption(row)
      const typeName = String(row?.GraduationType?.name || row?.type?.name || row?.gradeType?.name || 'Outros')
      return {
        ...option,
        id: Number(option.id),
        typeName,
      }
    }).filter((grade) => Number.isFinite(Number(grade.id)))

    return grades
  }

  const appendUniqueOption = <T extends OptionItem>(
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    nextOption: T,
  ) => {
    setter((prev) => {
      const alreadyExists = prev.some((item) => String(item.id) === String(nextOption.id) || normalizeText(item.name) === normalizeText(nextOption.name))
      if (alreadyExists) return prev
      return [nextOption, ...prev]
    })
  }

  const createStockMaterialOption = async (name: string): Promise<OptionItem> => {
    const response = await axios.post(`${gatewayBaseUrl}/backend/material`, { name: name.trim() })
    const created = toOption(response.data?.data || response.data || { name: name.trim() })
    appendUniqueOption(setMaterialOptions, created)
    return created
  }

  const createStockColorOption = async (name: string): Promise<ColorOption> => {
    const response = await axios.post(`${gatewayBaseUrl}/backend/color`, { name: name.trim() })
    const created = toColorOption(response.data?.data || response.data || { name: name.trim() })
    setFabricColorOptions((prev) => {
      const alreadyExists = prev.some((item) => String(item.id) === String(created.id) || normalizeText(item.name) === normalizeText(created.name))
      if (alreadyExists) return prev
      return [created, ...prev]
    })
    setTrimColorOptions((prev) => {
      const alreadyExists = prev.some((item) => String(item.id) === String(created.id) || normalizeText(item.name) === normalizeText(created.name))
      if (alreadyExists) return prev
      return [created, ...prev]
    })
    return created
  }

  const createResourceCategoryOption = async (name: string): Promise<OptionItem> => {
    const response = await axios.post(`${gatewayBaseUrl}/resources/resource/util/categories`, { name: name.trim() })
    const created = toOption(response.data?.data || response.data || { name: name.trim() })
    appendUniqueOption(setResourceCategoryOptions, created)
    return created
  }

  const createFashionColorOption = async (name: string): Promise<ColorOption> => {
    const normalizedName = name.trim()
    const localColorId = `draft-color-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
    const created: ColorOption = {
      id: localColorId,
      name: normalizedName,
      pantone: null,
      hex: undefined,
    }
    setCollectionColorOptions((prev) => {
      const alreadyExists = prev.some((item) => String(item.id) === String(created.id) || normalizeText(item.name) === normalizeText(created.name))
      if (alreadyExists) return prev
      return [created, ...prev]
    })
    return created
  }

  const createFashionMaterialOption = async (name: string): Promise<OptionItem> => {
    const trimmedName = name.trim()

    try {
      const response = await axios.post(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/materials`, { name: trimmedName })
      const created = toOption(response.data?.data || response.data || { name: trimmedName })
      appendUniqueOption(setMaterialOptions, created)
      return created
    } catch {
      const fallbackResponse = await axios.post(`${gatewayBaseUrl}/backend/material`, { name: trimmedName })
      const fallbackCreated = toOption(fallbackResponse.data?.data || fallbackResponse.data || { name: trimmedName })
      const refreshedMaterials = await fetchOptions('/backend-fashion-orders/fashion-product/materials')
      const matchedMaterial = refreshedMaterials.find((option) => normalizeText(option.name) === normalizeText(trimmedName))
      const resolvedMaterial = matchedMaterial || fallbackCreated
      const normalizedRefreshed = refreshedMaterials.filter((item) => Number.isFinite(Number(item.id)))
      setMaterialOptions(() => {
        const alreadyExists = normalizedRefreshed.some((item) => String(item.id) === String(resolvedMaterial.id) || normalizeText(item.name) === normalizeText(resolvedMaterial.name))
        if (alreadyExists) return normalizedRefreshed
        return [resolvedMaterial, ...normalizedRefreshed]
      })
      return resolvedMaterial
    }
  }

  const createFashionGroupOption = async (name: string): Promise<OptionItem> => {
    const response = await axios.post(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/groups`, { name: name.trim() })
    const created = toOption(response.data?.data || response.data || { name: name.trim() })
    appendUniqueOption(setGroupOptions, created)
    return created
  }

  const createFashionThemeOption = async (name: string): Promise<OptionItem> => {
    const response = await axios.post(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/themes`, {
      name: name.trim(),
      color: '#999999',
    })
    const created = toOption(response.data?.data || response.data || { name: name.trim() })
    appendUniqueOption(setCollectionThemeOptions, created)
    return created
  }

  const loadBaseOptionsByModule = async () => {
    if (!moduleType) return

    setIsLoadingOptions(true)
    setErrorMessage('')

    try {
      if (moduleType === 'collection') {
        const [grades, materials, categories, groups, themesResponse, resourcesResponse] = await Promise.all([
          fetchGradeOptions(),
          fetchOptions('/backend-fashion-orders/fashion-product/materials'),
          fetchOptions('/backend-fashion-orders/fashion-product/resource-categories'),
          fetchOptions('/backend-fashion-orders/fashion-product/groups'),
          axios.get(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/themes`),
          axios.get(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/resources`),
        ])

        const themesRows = parseArrayResponse(themesResponse.data)
        const normalizedThemes = themesRows
          .map(toOption)
          .filter((item) => Number.isFinite(Number(item.id)))

        const resourcesRows = parseArrayResponse(resourcesResponse.data)
        const materialNameById = new Map<number, string>(
          materials
            .map((material) => ({ id: Number(material.id), name: String(material.name || '') }))
            .filter((material) => Number.isFinite(material.id) && material.name)
            .map((material) => [material.id, material.name])
        )

        const categoryNameById = new Map<number, string>(
          categories
            .map((category) => ({ id: Number(category.id), name: String(category.name || '') }))
            .filter((category) => Number.isFinite(category.id) && category.name)
            .map((category) => [category.id, category.name])
        )

        const aviamentoByCategoryAndMaterial = new Map<string, CollectionAviamentoMaterialOption>()

        resourcesRows.forEach((row) => {
          const categoryId = Number(row?.categoryId || row?.categoryResourceId || row?.ResourceCategory?.id)
          const materialId = Number(row?.materialId || row?.Material?.id)
          if (!Number.isFinite(categoryId) || !Number.isFinite(materialId)) return

          const categoryName = String(
            categoryNameById.get(categoryId) ||
            row?.ResourceCategory?.name ||
            row?.categoryName ||
            `Categoria ${categoryId}`
          )

          const materialName = String(
            row?.Material?.name ||
            materialNameById.get(materialId) ||
            row?.materialName ||
            `Material ${materialId}`
          )

          const optionId = `${categoryId}-${materialId}`
          if (aviamentoByCategoryAndMaterial.has(optionId)) return

          aviamentoByCategoryAndMaterial.set(optionId, {
            id: optionId,
            name: `${categoryName} - ${materialName}`,
            categoryId,
            categoryName,
            materialId,
            materialName,
          })
        })

        const normalizedAviamentoOptions = Array.from(aviamentoByCategoryAndMaterial.values()).sort((a, b) =>
          a.name.localeCompare(b.name, 'pt-BR', { numeric: true })
        )

        setGradeOptions(grades)
        setMaterialOptions(materials.filter((item) => Number.isFinite(Number(item.id))))
        setGroupOptions(groups.filter((item) => Number.isFinite(Number(item.id))))
        setCollectionThemeOptions(normalizedThemes)
        setResourceCategoryOptions(categories.filter((item) => Number.isFinite(Number(item.id))))
        setCollectionAviamentoOptions(normalizedAviamentoOptions)
        setCollectionColorOptions([])
      }

      if (moduleType === 'family') {
        const collections = await fetchOptions('/backend-fashion-orders/fashion-product/collections', 'content', { limit: 100, offset: 0 })
        setCollectionOptions(collections.filter((item) => Number.isFinite(Number(item.id))))
      }

      if (moduleType === 'product') {
        const [collections, materials, units] = await Promise.all([
          fetchOptions('/backend-fashion-orders/fashion-product/collections', 'content', { limit: 100, offset: 0 }),
          fetchOptions('/backend-fashion-orders/fashion-product/materials'),
          fetchOptions('/resources/resource/util/units'),
        ])

        const filteredUnits = units.filter((unit) => {
          const normalized = normalizeText(String(unit.name || ''))
          return ['kg', 'm', 'mm', 'un', 'und', 'unidade'].includes(normalized)
        })

        setCollectionOptions(collections.filter((item) => Number.isFinite(Number(item.id))))
        setMaterialOptions(materials.filter((item) => Number.isFinite(Number(item.id))))
        setUnitOptions(filteredUnits.filter((item) => Number.isFinite(Number(item.id))))
      }

      if (moduleType === 'fabric') {
        const [materials, colors] = await Promise.all([
          fetchOptions('/backend/material/list/stock'),
          fetchColorOptions('/backend/color/list'),
        ])
        setMaterialOptions(materials.filter((item) => Number.isFinite(Number(item.id))))
        setFabricColorOptions(colors.filter((item) => String(item.name || '').trim() !== ''))
      }

      if (moduleType === 'trim') {
        const [categories, materials, colors, types, stockStreets, suppliers, units] = await Promise.all([
          fetchOptions('/resources/resource/util/categories'),
          fetchOptions('/backend/material/list/resource'),
          fetchColorOptions('/backend/color/list'),
          fetchOptions('/resources/resource/util/types'),
          fetchOptions('/backend/stockStreet/list'),
          fetchCompanyOptions(),
          fetchOptions('/resources/resource/util/units'),
        ])

        setResourceCategoryOptions(categories.filter((item) => Number.isFinite(Number(item.id))))
        setMaterialOptions(materials.filter((item) => Number.isFinite(Number(item.id))))
        setTrimColorOptions(colors.filter((item) => String(item.name || '').trim() !== ''))
        setTrimTypeOptions(types.filter((item) => Number.isFinite(Number(item.id))))
        setTrimStockStreetOptions(stockStreets.filter((item) => Number.isFinite(Number(item.id))))
        setTrimSupplierOptions(suppliers)
        setTrimPriceUnitOptions(units.filter((item) => Number.isFinite(Number(item.id))))
      }
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || 'Erro ao carregar opções para registro.')
    } finally {
      setIsLoadingOptions(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    loadBaseOptionsByModule()
  }, [isOpen, moduleType])

  useEffect(() => {
    if (!isOpen || moduleType !== 'family' || !familyCollectionId) return

    const loadFamilyDependencies = async () => {
      try {
        const [allGrades, collectionGradesResponse, collectionResponse, materialsResponse, groupsResponse, themesResponse] = await Promise.all([
          fetchGradeOptions(),
          axios.get(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/collections/${familyCollectionId}/grades`),
          axios.get(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/collections/${familyCollectionId}`),
          axios.get(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/collections/${familyCollectionId}/materials`),
          axios.get(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/collections/${familyCollectionId}/groups`),
          axios.get(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/collections/${familyCollectionId}/themes`),
        ])

        const collectionGradesRows = parseArrayResponse(collectionGradesResponse.data)
        const collectionGradeIds = collectionGradesRows
          .map((row) => Number(row?.gradeId || row?.Grade?.id || row?.grade?.id || row?.id))
          .filter((gradeId) => Number.isFinite(gradeId))

        const typedGrades = allGrades
          .filter((grade) => collectionGradeIds.includes(Number(grade.id)))
          .map((grade) => ({
            id: Number(grade.id),
            name: String(grade.name),
            typeName: String(grade.typeName || 'Coleção'),
          }))

        const materialsRows = parseArrayResponse(materialsResponse.data)
        const materials = materialsRows
          .map(toCollectionMaterialOption)
          .filter((item): item is OptionItem => item !== null)

        const collectionDetails = collectionResponse.data?.data || collectionResponse.data || {}
        const collectionColorRows = Array.isArray(collectionDetails?.CollectionColor)
          ? collectionDetails.CollectionColor
          : []
        const collectionColors: ColorOption[] = collectionColorRows.map(toColorOption)

        const groupRows = parseArrayResponse(groupsResponse.data)
        const normalizedGroups = groupRows
          .map((row) => row?.Group || row?.group || row)
          .filter(Boolean)
          .map(toOption)
          .filter((item) => Number.isFinite(Number(item.id)))

        const themeRows = parseArrayResponse(themesResponse.data)
        const normalizedThemes = themeRows
          .map(toOption)
          .filter((item) => Number.isFinite(Number(item.id)))

        setGradeOptions(typedGrades)
        setCollectionColorOptions(collectionColors.filter((item: ColorOption) => Number.isFinite(Number(item.id))))
        setCollectionMaterialOptions(materials.filter((item) => Number.isFinite(Number(item.id))))
        setFamilyGroupOptions(normalizedGroups)
        setFamilyThemeOptions(normalizedThemes)
        setSelectedFamilyGroupIds((prev) => prev.filter((id) => normalizedGroups.some((item) => Number(item.id) === id)))
        setSelectedFamilyThemeIds((prev) => prev.filter((id) => normalizedThemes.some((item) => Number(item.id) === id)))
      } catch (error: any) {
        setErrorMessage(error?.response?.data?.message || 'Erro ao carregar dados da coleção da família.')
      }
    }

    loadFamilyDependencies()
  }, [isOpen, moduleType, familyCollectionId])

  useEffect(() => {
    if (!isOpen || moduleType !== 'product' || !productCollectionId) return

    const loadProductDependencies = async () => {
      try {
        const [families, collectionResponse] = await Promise.all([
          fetchOptions('/backend-fashion-orders/fashion-product/families', 'content', { collectionId: productCollectionId, limit: 100, offset: 0 }),
          axios.get(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/collections/${productCollectionId}`),
        ])

        const collectionDetails = collectionResponse.data?.data || collectionResponse.data || {}
        const collectionColorRows = Array.isArray(collectionDetails?.CollectionColor)
          ? collectionDetails.CollectionColor
          : []
        const collectionColors = collectionColorRows.map(toColorOption)

        setFamilyOptions(families.filter((item: OptionItem) => Number.isFinite(Number(item.id))))
        setCollectionColorOptions(collectionColors.filter((item: ColorOption) => Number.isFinite(Number(item.id))))
      } catch (error: any) {
        setErrorMessage(error?.response?.data?.message || 'Erro ao carregar famílias e cores da coleção selecionada.')
      }
    }

    loadProductDependencies()
  }, [isOpen, moduleType, productCollectionId])

  useEffect(() => {
    if (!isOpen || moduleType !== 'product' || !productFamilyId) return

    const loadProductFamilyDependencies = async () => {
      try {
        const [groups, allGrades, familyGradesResponse, familyThemesResponse] = await Promise.all([
          fetchOptions('/backend-fashion-orders/fashion-product/groups', undefined, { familyId: productFamilyId, limit: 100, offset: 0 }),
          fetchGradeOptions(),
          axios.get(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/families/${productFamilyId}/grades`),
          axios.get(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/families/${productFamilyId}/themes`),
        ])

        const familyGradeRows = parseArrayResponse(familyGradesResponse.data)
        const familyGradeIds = familyGradeRows
          .map((row) => Number(row?.gradeId || row?.Grade?.id || row?.grade?.id || row?.id))
          .filter((gradeId) => Number.isFinite(gradeId))

        const productGrades = allGrades
          .filter((grade) => familyGradeIds.includes(Number(grade.id)))
          .map((grade) => ({
            id: Number(grade.id),
            name: String(grade.name),
            typeName: String(grade.typeName || 'Família'),
          }))

        const familyThemeRows = parseArrayResponse(familyThemesResponse.data)
        const productThemes = familyThemeRows
          .map(toOption)
          .filter((item) => Number.isFinite(Number(item.id)))

        setGroupOptions(groups.filter((item) => Number.isFinite(Number(item.id))))
        setProductGradeOptions(productGrades)
        setProductThemeOptions(productThemes)
        setSelectedProductGradeIds((prev) => prev.filter((id) => productGrades.some((item) => Number(item.id) === id)))
        setSelectedProductThemeId((prev) => (prev && productThemes.some((item) => Number(item.id) === prev) ? prev : null))
      } catch (error: any) {
        setErrorMessage(error?.response?.data?.message || 'Erro ao carregar conjuntos da família selecionada.')
      }
    }

    loadProductFamilyDependencies()
  }, [isOpen, moduleType, productFamilyId])

  useEffect(() => {
    if (!isOpen || moduleType !== 'trim') return

    const loadTrimTypes = async () => {
      try {
        const types = await fetchOptions('/resources/resource/util/types', undefined, trimCategoryId ? { categoryId: trimCategoryId } : undefined)
        const normalizedTypes = types.filter((item) => Number.isFinite(Number(item.id)))
        setTrimTypeOptions(normalizedTypes)

        setOptionalFieldValues((prev) => {
          const currentTypeId = Number(prev.typeId)
          if (!Number.isFinite(currentTypeId)) return prev
          const stillExists = normalizedTypes.some((item) => Number(item.id) === currentTypeId)
          if (stillExists) return prev
          const next = { ...prev }
          delete next.typeId
          return next
        })
      } catch {
        setTrimTypeOptions([])
      }
    }

    loadTrimTypes()
  }, [isOpen, moduleType, trimCategoryId])

  const gradesByType = useMemo(() => {
    const grouped: Record<string, GradeOption[]> = {}
    gradeOptions.forEach((grade) => {
      const typeName = grade.typeName || 'Outros'
      if (!grouped[typeName]) grouped[typeName] = []
      grouped[typeName].push(grade)
    })

    Object.keys(grouped).forEach((typeName) => {
      grouped[typeName].sort((a, b) => String(a.name).localeCompare(String(b.name), 'pt-BR', { numeric: true }))
    })

    return grouped
  }, [gradeOptions])

  useEffect(() => {
    const nextEnabled: Record<string, boolean> = {}
    Object.keys(gradesByType).forEach((typeName) => {
      const hasSelected = gradesByType[typeName].some((grade) => {
        const gradeId = Number(grade.id)
        if (moduleType === 'collection') return collectionGradeIds.includes(gradeId)
        if (moduleType === 'family') return familyGradeIds.includes(gradeId)
        return false
      })
      nextEnabled[typeName] = hasSelected
    })
    setEnabledGradeTypes(nextEnabled)
  }, [gradesByType, moduleType, collectionGradeIds, familyGradeIds])

  const toggleGradeType = (typeName: string, enabled: boolean) => {
    setEnabledGradeTypes((prev) => ({ ...prev, [typeName]: enabled }))
    const typeIds = (gradesByType[typeName] || []).map((grade) => Number(grade.id))

    if (!enabled) {
      if (moduleType === 'collection') {
        setCollectionGradeIds((prev) => prev.filter((id) => !typeIds.includes(id)))
      }
      if (moduleType === 'family') {
        setFamilyGradeIds((prev) => prev.filter((id) => !typeIds.includes(id)))
      }
    }
  }

  const toggleGradeSelection = (gradeId: number, typeName: string) => {
    if (!enabledGradeTypes[typeName]) return

    if (moduleType === 'collection') {
      setCollectionGradeIds((prev) => prev.includes(gradeId) ? prev.filter((id) => id !== gradeId) : [...prev, gradeId])
    }

    if (moduleType === 'family') {
      setFamilyGradeIds((prev) => prev.includes(gradeId) ? prev.filter((id) => id !== gradeId) : [...prev, gradeId])
    }
  }

  const addSelectedColor = (incoming: ColorOption) => {
    setCollectionSelectedColors((prev) => {
      const alreadyExists = prev.some((item) => String(item.id) === String(incoming.id) || normalizeText(item.name) === normalizeText(incoming.name))
      if (alreadyExists) return prev

      const nextColor: SelectedColor = {
        id: incoming.id,
        name: incoming.name,
        hex: incoming.hex,
        pantone: incoming.pantone || null,
      }

      if (!nextColor.pantone?.code) {
        setActiveCollectionColorId(nextColor.id)
      }

      return [...prev, nextColor]
    })
  }

  const updateSelectedColorPantone = (colorId: IdValue, pantone: PantoneValue | null) => {
    setCollectionSelectedColors((prev) => prev.map((color) => {
      if (String(color.id) !== String(colorId)) return color
      return { ...color, pantone, hex: pantone?.hex || color.hex }
    }))
  }

  const removeSelectedColor = (colorId: IdValue) => {
    setCollectionSelectedColors((prev) => prev.filter((color) => String(color.id) !== String(colorId)))
    if (String(activeCollectionColorId) === String(colorId)) {
      setActiveCollectionColorId(null)
    }
  }

  const addNumericSelection = (setter: React.Dispatch<React.SetStateAction<number[]>>, id: IdValue) => {
    const numericId = Number(id)
    if (!Number.isFinite(numericId)) return
    setter((prev) => prev.includes(numericId) ? prev : [...prev, numericId])
  }

  const removeNumericSelection = (setter: React.Dispatch<React.SetStateAction<number[]>>, id: number) => {
    setter((prev) => prev.filter((item) => item !== id))
  }

  const addCollectionAviamentoSelection = (id: IdValue) => {
    const selectedOption = collectionAviamentoOptions.find((item) => String(item.id) === String(id))
    if (!selectedOption) return
    setCollectionAviamentoSelections((prev) => prev.some((item) => item.id === selectedOption.id) ? prev : [...prev, selectedOption])
  }

  const removeCollectionAviamentoSelection = (id: IdValue) => {
    setCollectionAviamentoSelections((prev) => prev.filter((item) => String(item.id) !== String(id)))
  }

  const upsertProductComposition = (index: number, nextValue: Partial<ProductCompositionDraft>) => {
    setProductCompositions((prev) => prev.map((item, itemIndex) => itemIndex === index ? { ...item, ...nextValue } : item))
  }

  const addProductComposition = () => {
    setProductCompositions((prev) => [...prev, { materialId: null, usage: '', unitOfMeasurementId: null, collectionColorId: null }])
  }

  const removeProductComposition = (index: number) => {
    setProductCompositions((prev) => {
      if (prev.length === 1) return prev
      return prev.filter((_, itemIndex) => itemIndex !== index)
    })
  }

  const resetAll = () => {
    setErrorMessage('')

    setCollectionName('')
    setCollectionSelectedColors([])
    setActiveCollectionColorId(null)
    setCollectionGradeIds([])
    setCollectionMaterialIds([])
    setCollectionAviamentoOptions([])
    setCollectionAviamentoSelections([])
    setSelectedCollectionGroupIds([])
    setSelectedCollectionThemeIds([])

    setFamilyName('')
    setFamilyCollectionId(null)
    setFamilyGradeIds([])
    setFamilyColorIds([])
    setFamilyCollectionMaterialIds([])
    setFamilyGroupOptions([])
    setFamilyThemeOptions([])
    setSelectedFamilyGroupIds([])
    setSelectedFamilyThemeIds([])

    setProductCode('')
    setProductName('')
    setProductCollectionId(null)
    setProductFamilyId(null)
    setProductGroupId(null)
    setProductThemeOptions([])
    setSelectedProductThemeId(null)
    setProductGradeOptions([])
    setSelectedProductGradeIds([])
    setProductCompositions([{ materialId: null, usage: '', unitOfMeasurementId: null, collectionColorId: null }])

    setFabricCode('')
    setFabricMaterialId(null)
    setFabricItemColorId(null)
    setFabricColor('')
    setFabricPantone(null)

    setTrimName('')
    setTrimCategoryId(null)
    setTrimMaterialId(null)
    setTrimItemColorId(null)
    setTrimColor('')
    setTrimPantone(null)
    setTrimTypeOptions([])
    setTrimStockStreetOptions([])
    setTrimSupplierOptions([])
    setTrimPriceUnitOptions([])
    setSelectedTrimSupplierIds([])
    setTrimPriceValue('')
    setTrimPriceUnitId(null)
    setOptionalFieldValues({})

    setCollectionOptions([])
    setFamilyOptions([])
    setGroupOptions([])
    setCollectionThemeOptions([])
    setCollectionColorOptions([])
    setCollectionMaterialOptions([])
    setFabricColorOptions([])
    setTrimColorOptions([])
    setEnabledGradeTypes({})
  }

  useEffect(() => {
    setOptionalFieldValues((prev) => {
      if (optionalFieldConfigsByGrid.length === 0) return {}
      const allowedKeys = new Set(optionalFieldConfigsByGrid.map((field) => field.field))
      const next: Record<string, string> = {}
      Object.keys(prev).forEach((key) => {
        if (allowedKeys.has(key)) {
          next[key] = prev[key]
        }
      })
      return next
    })
  }, [optionalFieldConfigsByGrid])

  useEffect(() => {
    if (!isOpen) {
      resetAll()
      lastEmittedStageDraftRef.current = ''
      lastDraftSessionHandledForEmissionRef.current = ''
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      hasHydratedStageDraftRef.current = false
      hydratedStageDraftSessionRef.current = ''
      return
    }

    if (!moduleType || !autoCreateWhenReady) return

    const stageDraftSessionId = String(activeDraftId || '__single__')
    if (hasHydratedStageDraftRef.current && hydratedStageDraftSessionRef.current === stageDraftSessionId) {
      console.debug('[PIPELINE-WRITE-STAGED][FRONT][MODAL] hydrate-skip-same-session', {
        moduleType,
        stageDraftSessionId,
        stageDraftKeys: Object.keys(stageDraft || {}),
      })
      return
    }

    console.debug('[PIPELINE-WRITE-STAGED][FRONT][MODAL] hydrate-start', {
      moduleType,
      stageDraftSessionId,
      previousHydratedSession: hydratedStageDraftSessionRef.current,
      stageDraftKeys: Object.keys(stageDraft || {}),
    })

    const draft = stageDraft || {}

    if (moduleType === 'collection') {
      if (shouldRenderRequiredField('name')) {
        setCollectionName(String(draft.name || ''))
      }

      if (shouldRenderRequiredField('collectionColors')) {
        const nextCollectionColors = Array.isArray(draft.collectionColors)
          ? draft.collectionColors
              .map((color: any, index: number) => ({
                id: color?.id ?? `draft-color-${index}`,
                name: String(color?.name || '').trim(),
                pantone: color?.pantone?.code
                  ? {
                      code: String(color.pantone.code),
                      name: color.pantone?.name ? String(color.pantone.name) : undefined,
                      hex: color.pantone?.hex ? String(color.pantone.hex) : undefined,
                    }
                  : null,
                hex: color?.pantone?.hex,
              }))
              .filter((color: any) => color.name)
          : []
        setCollectionSelectedColors(nextCollectionColors)
      }

      if (shouldRenderRequiredField('gradeIds')) {
        setCollectionGradeIds(
          (Array.isArray(draft.gradeIds) ? draft.gradeIds : [])
            .map((item: any) => Number(item))
            .filter((item: number) => Number.isFinite(item))
        )
      }

      if (shouldRenderRequiredField('materials')) {
        setCollectionMaterialIds(
          (Array.isArray(draft.materialIds) ? draft.materialIds : [])
            .map((item: any) => Number(item))
            .filter((item: number) => Number.isFinite(item))
        )

        setCollectionAviamentoSelections(
          Array.isArray(draft.collectionAviamentoSelections)
            ? draft.collectionAviamentoSelections.filter((item: any) => item && item.categoryId && item.materialId)
            : []
        )
      }
    }

    if (moduleType === 'family') {
      if (shouldRenderRequiredField('name')) {
        setFamilyName(String(draft.name || ''))
      }

      if (shouldRenderRequiredField('collectionId')) {
        const parsedCollectionId = Number(draft.collectionId)
        setFamilyCollectionId(Number.isFinite(parsedCollectionId) ? parsedCollectionId : null)
      }

      if (shouldRenderRequiredField('collectionColorIds')) {
        setFamilyColorIds(
          (Array.isArray(draft.collectionColorIds) ? draft.collectionColorIds : [])
            .map((item: any) => Number(item))
            .filter((item: number) => Number.isFinite(item))
        )
      }

      if (shouldRenderRequiredField('gradeIds')) {
        setFamilyGradeIds(
          (Array.isArray(draft.gradeIds) ? draft.gradeIds : [])
            .map((item: any) => Number(item))
            .filter((item: number) => Number.isFinite(item))
        )
      }

      if (shouldRenderRequiredField('materials')) {
        setFamilyCollectionMaterialIds(
          (Array.isArray(draft.materials) ? draft.materials : [])
            .map((item: any) => Number(item))
            .filter((item: number) => Number.isFinite(item))
        )
      }
    }

    if (moduleType === 'product') {
      if (shouldRenderRequiredField('productCode')) {
        setProductCode(String(draft.productCode || ''))
      }

      if (shouldRenderRequiredField('name')) {
        setProductName(String(draft.name || ''))
      }

      if (shouldRenderRequiredField('collectionId')) {
        const parsedCollectionId = Number(draft.collectionId)
        setProductCollectionId(Number.isFinite(parsedCollectionId) ? parsedCollectionId : null)
      }

      if (shouldRenderRequiredField('familyId')) {
        const parsedFamilyId = Number(draft.familyId)
        setProductFamilyId(Number.isFinite(parsedFamilyId) ? parsedFamilyId : null)
      }

      if (shouldRenderRequiredField('groupId')) {
        const parsedGroupId = Number(draft.groupId)
        setProductGroupId(Number.isFinite(parsedGroupId) ? parsedGroupId : null)
      }

      if (shouldRenderRequiredField('gradeIds')) {
        setSelectedProductGradeIds(
          (Array.isArray(draft.gradeIds) ? draft.gradeIds : [])
            .map((item: any) => Number(item))
            .filter((item: number) => Number.isFinite(item))
        )
      }

      if (shouldRenderRequiredField('compositionItems')) {
        const compositionItems = Array.isArray(draft.compositionItems) ? draft.compositionItems : []
        const productItemColors = Array.isArray(draft.productItemColors) ? draft.productItemColors : []
        const maxRows = Math.max(compositionItems.length, productItemColors.length, 1)

        const nextCompositions: ProductCompositionDraft[] = Array.from({ length: maxRows }).map((_, index) => {
          const compositionItem = compositionItems[index] || {}
          const colorItem = productItemColors[index] || {}

          const materialId = Number(compositionItem.materialId ?? colorItem.materialId)
          const collectionColorId = Number(colorItem.collectionColorId)
          const unitOfMeasurementId = Number(compositionItem.unitOfMeasurementId)
          const usageValue = compositionItem.usage

          return {
            materialId: Number.isFinite(materialId) ? materialId : null,
            usage: usageValue === undefined || usageValue === null ? '' : String(usageValue),
            unitOfMeasurementId: Number.isFinite(unitOfMeasurementId) ? unitOfMeasurementId : null,
            collectionColorId: Number.isFinite(collectionColorId) ? collectionColorId : null,
          }
        })

        setProductCompositions(nextCompositions)
      }
    }

    if (moduleType === 'fabric') {
      const parsedMaterialId = Number(draft.materialId)
      const parsedItemColorId = Number(draft.itemColorId)
      const pantoneCode = String(draft.pantoneCode || '').trim()
      const pantoneName = String(draft.pantoneName || '').trim()
      const pantoneHex = String(draft.pantoneHex || '').trim()

      if (shouldRenderRequiredField('code')) {
        setFabricCode(String(draft.code || ''))
      }

      if (shouldRenderRequiredField('material')) {
        setFabricMaterialId(Number.isFinite(parsedMaterialId) ? parsedMaterialId : null)
      }

      if (shouldRenderRequiredField('color')) {
        setFabricItemColorId(Number.isFinite(parsedItemColorId) ? parsedItemColorId : null)
        setFabricColor(String(draft.color || ''))
      }

      if (shouldRenderRequiredField('pantoneCode')) {
        setFabricPantone(
          pantoneCode
            ? {
                code: pantoneCode,
                name: pantoneName || undefined,
                hex: pantoneHex || undefined,
              }
            : null
        )
      }
    }

    if (moduleType === 'trim') {
      if (shouldRenderRequiredField('name')) {
        setTrimName(String(draft.name || ''))
      }

      if (shouldRenderRequiredField('categoryId')) {
        const parsedCategoryId = Number(draft.categoryId)
        setTrimCategoryId(Number.isFinite(parsedCategoryId) ? parsedCategoryId : null)
      }

      if (shouldRenderRequiredField('materialId')) {
        const parsedMaterialId = Number(draft.materialId)
        setTrimMaterialId(Number.isFinite(parsedMaterialId) ? parsedMaterialId : null)
      }

      if (shouldRenderRequiredField('colors')) {
        const firstColor = Array.isArray(draft.colors) && draft.colors.length > 0 ? draft.colors[0] : null
        const nextColorName = String(firstColor?.colorName || '').trim()
        const nextPantoneCode = String(firstColor?.pantone?.code || '').trim()
        const nextPantoneName = String(firstColor?.pantone?.name || '').trim()
        const nextPantoneHex = String(firstColor?.pantone?.hex || '').trim()

        setTrimColor(nextColorName)
        setTrimPantone(
          nextPantoneCode
            ? {
                code: nextPantoneCode,
                name: nextPantoneName || undefined,
                hex: nextPantoneHex || undefined,
              }
            : null
        )
      }
    }

    if (optionalFieldConfigsByGrid.length > 0) {
      const nextOptionalValues: Record<string, string> = {}
      optionalFieldConfigsByGrid.forEach((fieldConfig) => {
        const rawValue = draft[fieldConfig.field]
        if (rawValue === undefined || rawValue === null) return
        if (typeof rawValue === 'string' && rawValue.trim() === '') return
        nextOptionalValues[fieldConfig.field] = String(rawValue)
      })
      setOptionalFieldValues(nextOptionalValues)
    }

    if (Array.isArray(draft.groupIds)) {
      const nextGroupIds = draft.groupIds
        .map((item: any) => Number(item))
        .filter((item: number) => Number.isFinite(item))
      if (moduleType === 'collection') setSelectedCollectionGroupIds(nextGroupIds)
      if (moduleType === 'family') setSelectedFamilyGroupIds(nextGroupIds)
    }

    if (Array.isArray(draft.themeIds)) {
      const nextThemeIds = draft.themeIds
        .map((item: any) => Number(item))
        .filter((item: number) => Number.isFinite(item))
      if (moduleType === 'collection') setSelectedCollectionThemeIds(nextThemeIds)
      if (moduleType === 'family') setSelectedFamilyThemeIds(nextThemeIds)
    }

    if (moduleType === 'product' && draft.themeId !== undefined) {
      const parsedThemeId = Number(draft.themeId)
      setSelectedProductThemeId(Number.isFinite(parsedThemeId) ? parsedThemeId : null)
    }

    if (moduleType === 'trim' && Array.isArray(draft.suppliers)) {
      setSelectedTrimSupplierIds(
        draft.suppliers
          .map((item: any) => Number(item))
          .filter((item: number) => Number.isFinite(item))
      )
    }

    if (moduleType === 'trim' && Array.isArray(draft.prices) && draft.prices.length > 0) {
      const firstPrice = draft.prices[0]
      const priceValue = Number(firstPrice?.price)
      const unitId = Number(firstPrice?.unitOfMeasurementId)
      setTrimPriceValue(Number.isFinite(priceValue) ? String(priceValue) : '')
      setTrimPriceUnitId(Number.isFinite(unitId) ? unitId : null)
    }

    hasHydratedStageDraftRef.current = true
    hydratedStageDraftSessionRef.current = stageDraftSessionId
    console.debug('[PIPELINE-WRITE-STAGED][FRONT][MODAL] hydrate-done', {
      moduleType,
      stageDraftSessionId,
      stageDraftKeys: Object.keys(draft || {}),
    })
  }, [activeDraftId, autoCreateWhenReady, isOpen, moduleType, optionalFieldConfigsByGrid, shouldRenderRequiredField, stageDraft])

  useEffect(() => {
    if (!moduleType || !autoCreateWhenReady || !onStageDraftChange) return

    const stageDraftSessionId = String(activeDraftId || '__single__')
    if (lastDraftSessionHandledForEmissionRef.current !== stageDraftSessionId) {
      lastDraftSessionHandledForEmissionRef.current = stageDraftSessionId
      lastEmittedStageDraftRef.current = JSON.stringify(stageDraft || {})
      console.debug('[PIPELINE-WRITE-STAGED][FRONT][MODAL] emit-skip-session-switch', {
        moduleType,
        stageDraftSessionId,
        stageDraftKeys: Object.keys(stageDraft || {}),
      })
      return
    }

    const nextStageDraft: Record<string, any> = {}

    if (moduleType === 'collection') {
      if (shouldRenderRequiredField('name')) {
        const value = String(collectionName || '').trim()
        if (value) nextStageDraft.name = value
      }

      if (shouldRenderRequiredField('collectionColors') && collectionSelectedColors.length > 0) {
        nextStageDraft.collectionColors = collectionSelectedColors
          .map((color) => ({
            name: String(color.name || '').trim(),
            pantone: {
              code: String(color.pantone?.code || '').trim(),
              name: color.pantone?.name,
              hex: color.pantone?.hex,
            },
          }))
          .filter((color) => color.name && color.pantone.code)
      }

      if (shouldRenderRequiredField('gradeIds') && collectionGradeIds.length > 0) {
        nextStageDraft.gradeIds = collectionGradeIds
      }

      if (shouldRenderRequiredField('materials')) {
        if (collectionMaterialIds.length > 0) {
          nextStageDraft.materialIds = collectionMaterialIds
        }
        if (collectionAviamentoSelections.length > 0) {
          nextStageDraft.collectionAviamentoSelections = collectionAviamentoSelections
        }
      }
    }

    if (moduleType === 'family') {
      if (shouldRenderRequiredField('name')) {
        const value = String(familyName || '').trim()
        if (value) nextStageDraft.name = value
      }

      if (shouldRenderRequiredField('collectionId') && familyCollectionId) {
        nextStageDraft.collectionId = familyCollectionId
      }

      if (shouldRenderRequiredField('collectionColorIds') && familyColorIds.length > 0) {
        nextStageDraft.collectionColorIds = familyColorIds
      }

      if (shouldRenderRequiredField('gradeIds') && familyGradeIds.length > 0) {
        nextStageDraft.gradeIds = familyGradeIds
      }

      if (shouldRenderRequiredField('materials') && familyCollectionMaterialIds.length > 0) {
        nextStageDraft.materials = familyCollectionMaterialIds
      }
    }

    if (moduleType === 'product') {
      if (shouldRenderRequiredField('productCode')) {
        const value = String(productCode || '').trim()
        if (value) nextStageDraft.productCode = value
      }

      if (shouldRenderRequiredField('name')) {
        const value = String(productName || '').trim()
        if (value) nextStageDraft.name = value
      }

      if (shouldRenderRequiredField('collectionId') && productCollectionId) {
        nextStageDraft.collectionId = productCollectionId
      }

      if (shouldRenderRequiredField('familyId') && productFamilyId) {
        nextStageDraft.familyId = productFamilyId
      }

      if (shouldRenderRequiredField('groupId') && productGroupId) {
        nextStageDraft.groupId = productGroupId
      }

      if (shouldRenderRequiredField('gradeIds') && selectedProductGradeIds.length > 0) {
        nextStageDraft.gradeIds = selectedProductGradeIds
      }

      if (shouldRenderRequiredField('compositionItems')) {
        const validCompositionItems = productCompositions
          .filter((item) => item.materialId && item.collectionColorId && item.unitOfMeasurementId && Number(item.usage) > 0)

        if (validCompositionItems.length > 0) {
          nextStageDraft.compositionItems = validCompositionItems.map((item) => ({
            tipo: 'materia-prima',
            materialId: Number(item.materialId),
            usage: Number(item.usage),
            unitOfMeasurementId: Number(item.unitOfMeasurementId),
          }))

          nextStageDraft.productItemColors = validCompositionItems.map((item) => ({
            collectionColorId: Number(item.collectionColorId),
            materialId: Number(item.materialId),
          }))
        }
      }
    }

    if (moduleType === 'fabric') {
      const selectedMaterial = materialOptions.find((item) => Number(item.id) === fabricMaterialId)

      if (shouldRenderRequiredField('code')) {
        const value = String(fabricCode || '').trim()
        if (value) nextStageDraft.code = value
      }

      if (shouldRenderRequiredField('material')) {
        if (fabricMaterialId && Number.isFinite(Number(fabricMaterialId))) {
          nextStageDraft.materialId = Number(fabricMaterialId)
        }
        const resolvedMaterialName = String(selectedMaterial?.name || stageDraft?.materialName || '').trim()
        if (resolvedMaterialName) {
          nextStageDraft.materialName = resolvedMaterialName
        }
      }

      if (shouldRenderRequiredField('color')) {
        const value = String(fabricColor || '').trim()
        if (value) nextStageDraft.color = value
        if (fabricItemColorId && Number.isFinite(Number(fabricItemColorId))) {
          nextStageDraft.itemColorId = Number(fabricItemColorId)
        }
      }

      if (shouldRenderRequiredField('pantoneCode')) {
        const pantoneCode = String(fabricPantone?.code || '').trim()
        if (pantoneCode) {
          nextStageDraft.pantoneCode = pantoneCode
        }
        const pantoneName = String(fabricPantone?.name || '').trim()
        if (pantoneName) {
          nextStageDraft.pantoneName = pantoneName
        }
        const pantoneHex = String(fabricPantone?.hex || '').trim()
        if (pantoneHex) {
          nextStageDraft.pantoneHex = pantoneHex
        }
      }
    }

    if (moduleType === 'trim') {
      if (shouldRenderRequiredField('name')) {
        const value = String(trimName || '').trim()
        if (value) nextStageDraft.name = value
      }

      if (shouldRenderRequiredField('categoryId') && trimCategoryId) {
        nextStageDraft.categoryId = trimCategoryId
      }

      if (shouldRenderRequiredField('materialId') && trimMaterialId) {
        nextStageDraft.materialId = trimMaterialId
      }

      if (shouldRenderRequiredField('colors')) {
        const colorName = String(trimColor || '').trim()
        const pantoneCode = String(trimPantone?.code || '').trim()
        if (colorName && pantoneCode) {
          nextStageDraft.colors = [
            {
              colorName,
              pantone: {
                code: pantoneCode,
                name: trimPantone?.name,
                hex: trimPantone?.hex,
              },
            },
          ]
        }
      }
    }

    const optionalFieldsPayload = buildOptionalFieldsPayload()
    Object.keys(optionalFieldsPayload).forEach((key) => {
      const value = optionalFieldsPayload[key]
      if (value === undefined || value === null) return
      if (typeof value === 'string' && value.trim() === '') return
      nextStageDraft[key] = value
    })

    const nextStageDraftSerialized = JSON.stringify(nextStageDraft)
    if (nextStageDraftSerialized === lastEmittedStageDraftRef.current) {
      console.debug('[PIPELINE-WRITE-STAGED][FRONT][MODAL] emit-skip-last-emitted', {
        moduleType,
        stageDraftSessionId,
        nextStageDraftKeys: Object.keys(nextStageDraft),
      })
      return
    }

    const currentStageDraftSerialized = JSON.stringify(stageDraft || {})
    if (nextStageDraftSerialized === currentStageDraftSerialized) {
      lastEmittedStageDraftRef.current = nextStageDraftSerialized
      console.debug('[PIPELINE-WRITE-STAGED][FRONT][MODAL] emit-skip-equals-prop-stage-draft', {
        moduleType,
        stageDraftSessionId,
        nextStageDraftKeys: Object.keys(nextStageDraft),
      })
      return
    }

    lastEmittedStageDraftRef.current = nextStageDraftSerialized
    console.debug('[PIPELINE-WRITE-STAGED][FRONT][MODAL] emit-stage-draft-change', {
      moduleType,
      stageDraftSessionId,
      nextStageDraftKeys: Object.keys(nextStageDraft),
      currentStageDraftKeys: Object.keys(stageDraft || {}),
    })
    onStageDraftChange(nextStageDraft)
  }, [
    activeDraftId,
    autoCreateWhenReady,
    collectionAviamentoSelections,
    collectionGradeIds,
    selectedCollectionGroupIds,
    collectionMaterialIds,
    collectionName,
    collectionSelectedColors,
    selectedCollectionThemeIds,
    familyCollectionId,
    familyCollectionMaterialIds,
    familyColorIds,
    familyGradeIds,
    selectedFamilyGroupIds,
    familyName,
    selectedFamilyThemeIds,
    fabricCode,
    fabricColor,
    fabricItemColorId,
    fabricMaterialId,
    fabricPantone,
    materialOptions,
    moduleType,
    onStageDraftChange,
    optionalFieldConfigsByGrid,
    optionalFieldValues,
    productCode,
    productCollectionId,
    productCompositions,
    productFamilyId,
    productGroupId,
    productName,
    selectedProductThemeId,
    selectedProductGradeIds,
    shouldRenderRequiredField,
    stageDraft,
    trimCategoryId,
    trimColor,
    trimMaterialId,
    trimName,
    trimPantone,
    selectedTrimSupplierIds,
    trimPriceValue,
    trimPriceUnitId,
  ])

  const validateBeforeSubmit = (): string | null => {
    if (!moduleType) return 'Módulo inválido para registro.'

    if (moduleType === 'collection') {
      const requireName = shouldRenderRequiredField('name')
      const requireCollectionColors = shouldRenderRequiredField('collectionColors')
      const requireGrades = shouldRenderRequiredField('gradeIds')
      const requireMaterials = shouldRenderRequiredField('materials')

      if (requireName && !collectionName.trim()) return 'Preencha o nome da coleção.'
      if (requireCollectionColors && collectionSelectedColors.length === 0) return 'Selecione as cores da coleção.'
      if (requireCollectionColors && collectionSelectedColors.some((color) => !color.pantone?.code)) return 'Defina o Pantone para todas as cores da coleção.'
      if (requireGrades && collectionGradeIds.length === 0) return 'Selecione ao menos uma grade da coleção.'
      if (requireMaterials && collectionMaterialIds.length === 0) return 'Selecione ao menos um tecido para a coleção.'
      if (requireMaterials && collectionAviamentoSelections.length === 0) return 'Selecione ao menos um material de aviamento para a coleção.'
      return null
    }

    if (moduleType === 'family') {
      const requireName = shouldRenderRequiredField('name')
      const requireCollectionId = shouldRenderRequiredField('collectionId')
      const requireCollectionColorIds = shouldRenderRequiredField('collectionColorIds')
      const requireGrades = shouldRenderRequiredField('gradeIds')
      const requireMaterials = shouldRenderRequiredField('materials')

      if (requireName && !familyName.trim()) return 'Preencha o nome da família.'
      if (requireCollectionId && !familyCollectionId) return 'Selecione a coleção vinculada da família.'
      if (requireCollectionColorIds && familyColorIds.length === 0) return 'Selecione as cores da coleção para a família.'
      if (requireGrades && familyGradeIds.length === 0) return 'Selecione ao menos uma grade da família.'
      if (requireMaterials && familyCollectionMaterialIds.length === 0) return 'Selecione ao menos um conjunto de tecidos e aviamento da família.'
      return null
    }

    if (moduleType === 'product') {
      const requireProductCode = shouldRenderRequiredField('productCode')
      const requireName = shouldRenderRequiredField('name')
      const requireCollectionId = shouldRenderRequiredField('collectionId')
      const requireFamilyId = shouldRenderRequiredField('familyId')
      const requireGroupId = shouldRenderRequiredField('groupId')
      const requireGradeIds = shouldRenderRequiredField('gradeIds')
      const requireCompositionItems = shouldRenderRequiredField('compositionItems')

      if (requireProductCode && !productCode.trim()) return 'Preencha o código do produto.'
      if (requireName && !productName.trim()) return 'Preencha o nome do produto.'
      if (requireCollectionId && !productCollectionId) return 'Selecione a coleção vinculada do produto.'
      if (requireFamilyId && !productFamilyId) return 'Selecione a família do produto.'
      if (requireGroupId && !productGroupId) return 'Selecione o conjunto do produto.'
      if (requireGradeIds && selectedProductGradeIds.length === 0) return 'Selecione ao menos uma grade do produto.'

      if (requireCompositionItems) {
        const invalidComposition = productCompositions.some((item) => {
          return !item.materialId || !item.collectionColorId || !item.unitOfMeasurementId || Number(item.usage) <= 0
        })

        if (invalidComposition) return 'Preencha consumo, unidade de medida, material e cor em todos os itens da composição.'
      }
      return null
    }

    if (moduleType === 'fabric') {
      const requireCode = shouldRenderRequiredField('code')
      const requireMaterial = shouldRenderRequiredField('material')
      const requireColor = shouldRenderRequiredField('color')
      const requirePantone = shouldRenderRequiredField('pantoneCode')

      if (requireCode && !fabricCode.trim()) return 'Preencha o código do tecido.'
      if (requireMaterial && !fabricMaterialId) return 'Selecione o material do tecido.'
      if (requireColor && !fabricColor.trim()) return 'Preencha a cor do tecido.'
      if (requirePantone && !fabricPantone?.code) return 'Selecione o Pantone do tecido.'
      return null
    }

    if (moduleType === 'trim') {
      const requireName = shouldRenderRequiredField('name')
      const requireCategoryId = shouldRenderRequiredField('categoryId')
      const requireMaterialId = shouldRenderRequiredField('materialId')
      const requireColors = shouldRenderRequiredField('colors')

      if (requireName && !trimName.trim()) return 'Preencha o nome do aviamento.'
      if (requireCategoryId && !trimCategoryId) return 'Selecione a categoria do aviamento.'
      if (requireMaterialId && !trimMaterialId) return 'Selecione o material do aviamento.'
      if (requireColors && !trimColor.trim()) return 'Preencha a cor do aviamento.'
      if (requireColors && !trimPantone?.code) return 'Selecione o Pantone do aviamento.'
      return null
    }

    return null
  }

  const submitCollection = async () => {
    const optionalFieldsPayload = buildOptionalFieldsPayload()
    const payload: Record<string, any> = {
      ...optionalFieldsPayload,
    }

    if (shouldRenderRequiredField('name')) {
      payload.name = collectionName.trim()
    }

    if (shouldRenderRequiredField('gradeIds')) {
      payload.gradeIds = collectionGradeIds
    }

    if (shouldRenderRequiredField('materials')) {
      payload.materials = collectionMaterialIds.map((materialId) => ({
        materialId,
        categoryMaterials: collectionAviamentoSelections.map((aviamentoSelection) => ({
          categoryId: aviamentoSelection.categoryId,
          materialId: aviamentoSelection.materialId,
        })),
      }))
    }

    if (shouldRenderRequiredField('collectionColors')) {
      payload.collectionColors = collectionSelectedColors.map((color) => ({
        name: color.name.trim(),
        pantone: {
          code: String(color.pantone?.code || '').trim(),
          name: color.pantone?.name,
        },
      }))
    }

    const response = await axios.post(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/collections`, payload)
    const created = response.data?.data || response.data
    onCreated({ id: Number(created?.id), name: created?.name || collectionName.trim(), ...created })
  }

  const submitFamily = async () => {
    const optionalFieldsPayload = buildOptionalFieldsPayload()
    const payload: Record<string, any> = {
      ...optionalFieldsPayload,
    }

    if (shouldRenderRequiredField('name')) {
      payload.name = familyName.trim()
    }

    if (shouldRenderRequiredField('collectionId')) {
      payload.collectionId = Number(familyCollectionId)
    }

    if (shouldRenderRequiredField('gradeIds')) {
      payload.gradeIds = familyGradeIds
    }

    if (shouldRenderRequiredField('collectionColorIds')) {
      payload.collectionColorIds = familyColorIds
    }

    if (shouldRenderRequiredField('materials')) {
      payload.materials = familyCollectionMaterialIds.map((collectionMaterialId) => ({ collectionMaterialId }))
    }

    const response = await axios.post(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product/families`, payload)
    const created = response.data?.data || response.data
    onCreated({ id: Number(created?.id), name: created?.name || familyName.trim(), ...created })
  }

  const submitProduct = async () => {
    const optionalFieldsPayload = buildOptionalFieldsPayload()
    const payload: Record<string, any> = {
      ...optionalFieldsPayload,
    }

    if (shouldRenderRequiredField('productCode')) {
      payload.productCode = productCode.trim()
    }

    if (shouldRenderRequiredField('name')) {
      payload.name = productName.trim()
    }

    if (shouldRenderRequiredField('collectionId')) {
      payload.collectionId = Number(productCollectionId)
    }

    if (shouldRenderRequiredField('familyId')) {
      payload.familyId = Number(productFamilyId)
    }

    if (shouldRenderRequiredField('groupId')) {
      payload.groupId = Number(productGroupId)
    }

    if (shouldRenderRequiredField('gradeIds')) {
      payload.gradeIds = selectedProductGradeIds
    }

    if (shouldRenderRequiredField('compositionItems')) {
      payload.compositionItems = productCompositions.map((item) => ({
        tipo: 'materia-prima',
        materialId: Number(item.materialId),
        usage: Number(item.usage),
        unitOfMeasurementId: Number(item.unitOfMeasurementId),
      }))

      payload.productItemColors = productCompositions.map((item) => ({
        collectionColorId: Number(item.collectionColorId),
        materialId: Number(item.materialId),
      }))
    }

    const response = await axios.post(`${gatewayBaseUrl}/backend-fashion-orders/fashion-product`, payload)
    const created = response.data?.data || response.data
    onCreated({
      id: Number(created?.id),
      name: created?.name || productName.trim(),
      productCode: created?.productCode || productCode.trim(),
      ...created,
    })
  }

  const submitFabric = async () => {
    const optionalFieldsPayload = buildOptionalFieldsPayload()
    const selectedMaterial = materialOptions.find((item) => Number(item.id) === fabricMaterialId)
    const parsedItemColorId = Number(fabricItemColorId)
    const itemColorId = Number.isFinite(parsedItemColorId) ? parsedItemColorId : undefined

    const payloadMeasurementUnit = String(optionalFieldsPayload.measurementUnit || 'KG').toUpperCase()
    const normalizedMeasurementUnit = payloadMeasurementUnit === 'MM' || payloadMeasurementUnit === 'UN' ? payloadMeasurementUnit : 'KG'

    const quantityValue = Number(optionalFieldsPayload.quantity)
    const totalWeightValue = Number(optionalFieldsPayload.totalWeight)
    const lengthInMmValue = Number(optionalFieldsPayload.lengthInMm)

    const normalizedQuantity = normalizedMeasurementUnit === 'UN' && Number.isFinite(quantityValue) ? quantityValue : undefined
    const normalizedTotalWeight = normalizedMeasurementUnit === 'KG' && Number.isFinite(totalWeightValue) ? totalWeightValue : 0
    const normalizedLengthInMm = normalizedMeasurementUnit === 'MM' && Number.isFinite(lengthInMmValue) ? lengthInMmValue : undefined

    const {
      measurementUnit: _ignoredMeasurementUnit,
      quantity: _ignoredQuantity,
      totalWeight: _ignoredTotalWeight,
      lengthInMm: _ignoredLengthInMm,
      ...fabricOptionalWithoutUnit
    } = optionalFieldsPayload

    const payload = {
      code: fabricCode.trim(),
      material: selectedMaterial?.name || '',
      measurementUnit: normalizedMeasurementUnit,
      quantity: normalizedQuantity,
      totalWeight: normalizedTotalWeight,
      lengthInMm: normalizedLengthInMm,
      itemColorId,
      color: fabricColor.trim(),
      pantoneCode: String(fabricPantone?.code || '').trim(),
      pantoneName: fabricPantone?.name,
      ...fabricOptionalWithoutUnit,
    }

    const response = await axios.post(`${gatewayBaseUrl}/backend/stock-fashion`, payload)
    const created = response.data?.data || response.data
    const createdId = Number(created?.id || created?.codeId)
    const createdDescription = String(created?.description || created?.material || fabricCode.trim())
    const createdColor = String(
      created?.color ||
      created?.itemColor?.name ||
      created?.ItemColor?.name ||
      fabricColor.trim() ||
      ''
    )
    const createdPantoneCode = String(
      created?.pantoneCode ||
      created?.itemColor?.Pantone?.code ||
      created?.ItemColor?.Pantone?.code ||
      fabricPantone?.code ||
      ''
    )
    const createdDisplayName = [createdDescription, createdColor].filter(Boolean).join(' - ')
    const createdName = [createdDisplayName, createdPantoneCode ? `(${createdPantoneCode})` : ''].filter(Boolean).join(' ')
    onCreated({
      id: createdId,
      name: createdName || createdDescription,
      _displayName: createdName || createdDescription,
      color: createdColor,
      pantoneCode: createdPantoneCode,
      _swatchHex:
        created?.pantoneHex ||
        created?.itemColor?.Pantone?.hex ||
        created?.ItemColor?.Pantone?.hex ||
        fabricPantone?.hex,
      ...created,
    })
  }

  const submitTrim = async () => {
    const optionalFieldsPayload = buildOptionalFieldsPayload()

    const payloadStockUnit = String(optionalFieldsPayload.stockUnit || 'UN').toUpperCase()
    const normalizedStockUnit = payloadStockUnit === 'KG' || payloadStockUnit === 'MM' ? payloadStockUnit : 'UN'

    const unitQuantityValue = Number(optionalFieldsPayload.unitQuantity)
    const totalWeightValue = Number(optionalFieldsPayload.totalWeight)
    const lengthInMmValue = Number(optionalFieldsPayload.lengthInMm)

    const normalizedUnitQuantity = normalizedStockUnit === 'UN' && Number.isFinite(unitQuantityValue) ? unitQuantityValue : 0
    const normalizedTotalWeight = normalizedStockUnit === 'KG' && Number.isFinite(totalWeightValue) ? totalWeightValue : undefined
    const normalizedLengthInMm = normalizedStockUnit === 'MM' && Number.isFinite(lengthInMmValue) ? lengthInMmValue : undefined

    const {
      stockUnit: _ignoredStockUnit,
      unitQuantity: _ignoredUnitQuantity,
      totalWeight: _ignoredTrimTotalWeight,
      lengthInMm: _ignoredTrimLengthInMm,
      ...trimOptionalWithoutUnit
    } = optionalFieldsPayload

    const payload: Record<string, any> = {
      stockUnit: normalizedStockUnit,
      unitQuantity: normalizedUnitQuantity,
      totalWeight: normalizedTotalWeight,
      lengthInMm: normalizedLengthInMm,
      ...trimOptionalWithoutUnit,
    }

    if (shouldRenderRequiredField('name')) {
      payload.name = trimName.trim()
    }

    if (shouldRenderRequiredField('categoryId')) {
      payload.categoryId = Number(trimCategoryId)
    }

    if (shouldRenderRequiredField('materialId')) {
      payload.materialId = Number(trimMaterialId)
    }

    if (shouldRenderRequiredField('colors')) {
      payload.colors = [
        {
          colorName: trimColor.trim(),
          pantone: {
            code: String(trimPantone?.code || '').trim(),
            name: trimPantone?.name,
          },
        },
      ]
    }

    const response = await axios.post(`${gatewayBaseUrl}/resources/resource`, payload)
    const created = response.data?.data || response.data
    onCreated({ id: Number(created?.id), name: created?.name || trimName.trim(), ...created })
  }

  const handleSubmit = async () => {
    setErrorMessage('')
    const validationError = validateBeforeSubmit()
    if (validationError) {
      setErrorMessage(validationError)
      return
    }

    setIsSubmitting(true)
    try {
      if (moduleType === 'collection') await submitCollection()
      if (moduleType === 'family') await submitFamily()
      if (moduleType === 'product') await submitProduct()
      if (moduleType === 'fabric') await submitFabric()
      if (moduleType === 'trim') await submitTrim()
      if (inline) {
        resetAll()
        loadBaseOptionsByModule()
      } else {
        onClose()
      }
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || 'Erro ao criar registro integrado.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const modalTitle = useMemo(() => {
    if (moduleType === 'collection') return 'Criar coleção'
    if (moduleType === 'family') return 'Criar família'
    if (moduleType === 'product') return 'Criar produto'
    if (moduleType === 'fabric') return 'Criar tecido'
    if (moduleType === 'trim') return 'Criar aviamento'
    return 'Criar registro'
  }, [moduleType])

  const selectedCollectionColor = useMemo(() => {
    if (activeCollectionColorId == null) return null
    return collectionSelectedColors.find((color) => String(color.id) === String(activeCollectionColorId)) || null
  }, [activeCollectionColorId, collectionSelectedColors])

  if ((!isOpen && !inline) || !moduleType) return null

  const validationMessage = validateBeforeSubmit()
  const createButtonDisabled = Boolean(validationMessage) || isSubmitting || isLoadingOptions

  return (
    <Overlay $inline={inline}>
      <Container $inline={inline}>
        <Header $inline={inline}>
          <Title $inline={inline}>{modalTitle}</Title>
          {!inline && (
            <CloseButton type='button' onClick={onClose}>
              <X size={18} />
            </CloseButton>
          )}
        </Header>

        <Body $inline={inline}>
          {errorMessage && <ErrorBox>{errorMessage}</ErrorBox>}
          {isLoadingOptions && <LoadingText>Carregando opções...</LoadingText>}

          {!isLoadingOptions && autoCreateWhenReady && (
            <DraftSessionCard>
              <FieldGroup>
                <FieldLabel>Rascunho do registro</FieldLabel>
                <FormGrid cols={2}>
                  <FieldSelect
                    value={activeDraftId || ''}
                    onChange={(event) => {
                      const nextDraftId = String(event.target.value || '')
                      if (!nextDraftId || !onSelectDraft) return
                      console.debug('[PIPELINE-WRITE-STAGED][FRONT][MODAL] draft-select-change', {
                        moduleType,
                        activeDraftId,
                        nextDraftId,
                        draftOptionIds: draftOptions.map((item) => item.id),
                      })
                      onSelectDraft(nextDraftId)
                    }}
                  >
                    {draftOptions.length === 0 && <option value=''>Rascunho atual</option>}
                    {draftOptions.map((draftOption) => (
                      <option key={`draft-option-${draftOption.id}`} value={draftOption.id}>{draftOption.label}</option>
                    ))}
                  </FieldSelect>

                  <InlineActionButton
                    type='button'
                    onClick={() => {
                      if (!onCreateDraft) return
                      onCreateDraft()
                    }}
                  >
                    Novo item em andamento
                  </InlineActionButton>
                </FormGrid>
              </FieldGroup>
            </DraftSessionCard>
          )}

          {!isLoadingOptions && moduleType === 'collection' && (
            <>
              {(shouldRenderRequiredField('name') || shouldRenderRequiredField('collectionColors')) && (
                <Card $inline={inline}>
                  {!inline && <CardTitle>Geral</CardTitle>}
                  <FormGrid cols={2}>
                    {shouldRenderRequiredField('name') && (
                      <FieldGroup>
                        <FieldLabel>Nome da coleção</FieldLabel>
                        <FieldInput
                          value={collectionName}
                          onChange={(event) => setCollectionName(event.target.value)}
                          placeholder='Digite aqui'
                        />
                      </FieldGroup>
                    )}

                    {shouldRenderRequiredField('collectionColors') && (
                      <FieldGroup>
                        <FieldLabel>Cores da coleção</FieldLabel>
                        <InfiniteSelect
                          placeholder='Selecione cor'
                          mode='crud'
                          value={null}
                          allowDelete={false}
                          showCreateOnlyIfNotExists={true}
                          showSelectedBelow={false}
                          fetchFunction={createLocalFetch(collectionColorOptions)}
                          onSelect={(option) => {
                            if (!option) return
                            const optionEntry = collectionColorOptions.find((item) => String(item.id) === String(option.id)) || { id: option.id, name: option.name, pantone: null }
                            addSelectedColor(optionEntry)
                          }}
                          onItemCreate={async (name) => {
                            const created = await createFashionColorOption(name)
                            addSelectedColor(created)
                            return created
                          }}
                          getOptionLabel={(option) => option.name}
                        />

                        {collectionSelectedColors.length > 0 && (
                          <TagsContainer>
                            {collectionSelectedColors.map((color) => (
                              <Tag
                                key={`collection-color-${String(color.id)}`}
                                onClick={() => {
                                  if (!color.pantone?.code) {
                                    setActiveCollectionColorId(color.id)
                                  }
                                }}
                                $clickable={!color.pantone?.code}
                                $active={String(activeCollectionColorId) === String(color.id)}
                              >
                                {color.pantone?.hex && <Swatch color={color.pantone.hex} />}
                                <span>{color.name}</span>
                                {color.pantone?.code && <TagHint>({color.pantone.code})</TagHint>}
                                {!color.pantone?.code && <TagError>sem Pantone</TagError>}
                                <RemoveTagButton
                                  type='button'
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    removeSelectedColor(color.id)
                                  }}
                                >
                                  ×
                                </RemoveTagButton>
                              </Tag>
                            ))}
                          </TagsContainer>
                        )}

                        {selectedCollectionColor && !selectedCollectionColor.pantone?.code && (
                          <PantoneContainer>
                            <FieldLabel>Pantone para {selectedCollectionColor.name}</FieldLabel>
                            <PantoneSelect
                              value={selectedCollectionColor.pantone}
                              onSelect={(pantone) => updateSelectedColorPantone(selectedCollectionColor.id, pantone)}
                            />
                          </PantoneContainer>
                        )}
                      </FieldGroup>
                    )}
                  </FormGrid>
                </Card>
              )}

              {shouldRenderRequiredField('gradeIds') && (
                <Card $inline={inline}>
                  {!inline && <CardTitle>Grades</CardTitle>}
                  {Object.keys(gradesByType).map((typeName) => (
                    <GradeTypeBlock key={`collection-grade-type-${typeName}`}>
                      <GradeTypeHeader>
                        <SwitchContainer>
                          <SwitchInput
                            type='checkbox'
                            checked={enabledGradeTypes[typeName] || false}
                            onChange={(event) => toggleGradeType(typeName, event.target.checked)}
                          />
                          <SwitchTrack $checked={enabledGradeTypes[typeName] || false}>
                            <SwitchThumb $checked={enabledGradeTypes[typeName] || false} />
                          </SwitchTrack>
                        </SwitchContainer>
                        <GradeTypeLabel>{typeName}</GradeTypeLabel>
                      </GradeTypeHeader>

                      <GradeButtons>
                        {gradesByType[typeName].map((grade) => {
                          const gradeId = Number(grade.id)
                          const selected = collectionGradeIds.includes(gradeId)
                          return (
                            <GradeButton
                              key={`collection-grade-${gradeId}`}
                              type='button'
                              disabled={!enabledGradeTypes[typeName]}
                              $selected={selected}
                              onClick={() => toggleGradeSelection(gradeId, typeName)}
                            >
                              {grade.name}
                            </GradeButton>
                          )
                        })}
                      </GradeButtons>
                    </GradeTypeBlock>
                  ))}
                </Card>
              )}

              {shouldRenderRequiredField('materials') && (
                <Card $inline={inline}>
                  {!inline && <CardTitle>Tecidos e Aviamentos (Conjuntos)</CardTitle>}
                  <FormGrid cols={2}>
                    <FieldGroup>
                      <FieldLabel>Tecidos</FieldLabel>
                      <InfiniteSelect
                        placeholder='Selecione tecido'
                        mode='crud'
                        allowDelete={false}
                        showCreateOnlyIfNotExists={true}
                        value={null}
                        showSelectedBelow={false}
                        fetchFunction={createLocalFetch(materialOptions)}
                        onSelect={(option) => {
                          if (!option) return
                          addNumericSelection(setCollectionMaterialIds, option.id)
                        }}
                        onItemCreate={async (name) => {
                          const created = await createFashionMaterialOption(name)
                          addNumericSelection(setCollectionMaterialIds, created.id)
                          return created
                        }}
                        getOptionLabel={(option) => option.name}
                      />
                      <TagsContainer>
                        {collectionMaterialIds.map((materialId) => {
                          const label = materialOptions.find((item) => Number(item.id) === materialId)?.name || `ID ${materialId}`
                          return (
                            <Tag key={`collection-material-${materialId}`}>
                              <span>{label}</span>
                              <RemoveTagButton type='button' onClick={() => removeNumericSelection(setCollectionMaterialIds, materialId)}>×</RemoveTagButton>
                            </Tag>
                          )
                        })}
                      </TagsContainer>
                    </FieldGroup>

                    <FieldGroup>
                      <FieldLabel>Aviamentos</FieldLabel>
                      <InfiniteSelect
                        placeholder='Selecione material de aviamento'
                        mode='readonly'
                        value={null}
                        showSelectedBelow={false}
                        fetchFunction={createLocalFetch(collectionAviamentoOptions)}
                        onSelect={(option) => {
                          if (!option) return
                          addCollectionAviamentoSelection(option.id)
                        }}
                        getOptionLabel={(option) => option.name}
                      />
                      <TagsContainer>
                        {collectionAviamentoSelections.map((aviamentoSelection) => {
                          const label = aviamentoSelection.name
                          return (
                            <Tag key={`collection-aviamento-${aviamentoSelection.id}`}>
                              <span>{label}</span>
                              <RemoveTagButton type='button' onClick={() => removeCollectionAviamentoSelection(aviamentoSelection.id)}>×</RemoveTagButton>
                            </Tag>
                          )
                        })}
                      </TagsContainer>
                    </FieldGroup>
                  </FormGrid>
                </Card>
              )}
            </>
          )}

          {!isLoadingOptions && moduleType === 'family' && (
            <>
              {(shouldRenderRequiredField('name') || shouldRenderRequiredField('collectionId') || shouldRenderRequiredField('collectionColorIds') || shouldRenderRequiredField('materials')) && (
                <Card $inline={inline}>
                  {!inline && <CardTitle>Geral</CardTitle>}
                  <FormGrid cols={2}>
                    {shouldRenderRequiredField('name') && (
                      <FieldGroup>
                        <FieldLabel>Nome da família</FieldLabel>
                        <FieldInput
                          value={familyName}
                          onChange={(event) => setFamilyName(event.target.value)}
                          placeholder='Digite aqui'
                        />
                      </FieldGroup>
                    )}

                    {shouldRenderRequiredField('collectionId') && (
                      <FieldGroup>
                        <FieldLabel>Coleção vinculada</FieldLabel>
                        <InfiniteSelect
                          placeholder='Selecione coleção'
                          mode='readonly'
                          showSelectedBelow={false}
                          value={familyCollectionId
                            ? collectionOptions.find((item) => Number(item.id) === familyCollectionId) || null
                            : null}
                          fetchFunction={createLocalFetch(collectionOptions)}
                          onSelect={(option) => {
                            const nextId = option ? Number(option.id) : null
                            setFamilyCollectionId(nextId)
                            setFamilyGradeIds([])
                            setFamilyColorIds([])
                            setFamilyCollectionMaterialIds([])
                            setSelectedFamilyGroupIds([])
                            setSelectedFamilyThemeIds([])
                          }}
                          getOptionLabel={(option) => option.name}
                        />
                      </FieldGroup>
                    )}

                    {shouldRenderRequiredField('collectionColorIds') && (
                      <FieldGroup>
                        <FieldLabel>Cores da coleção</FieldLabel>
                        <InfiniteSelect
                          placeholder='Selecione cor'
                          mode='readonly'
                          showSelectedBelow={false}
                          value={null}
                          disabled={!familyCollectionId}
                          fetchFunction={createLocalFetch(collectionColorOptions)}
                          onSelect={(option) => {
                            if (!option) return
                            addNumericSelection(setFamilyColorIds, option.id)
                          }}
                          getOptionLabel={(option) => option.name}
                        />
                        <TagsContainer>
                          {familyColorIds.map((colorId) => {
                            const color = collectionColorOptions.find((item) => Number(item.id) === colorId)
                            return (
                              <Tag key={`family-color-${colorId}`}>
                                {color?.hex && <Swatch color={color.hex} />}
                                <span>{color?.name || `ID ${colorId}`}</span>
                                <RemoveTagButton type='button' onClick={() => removeNumericSelection(setFamilyColorIds, colorId)}>×</RemoveTagButton>
                              </Tag>
                            )
                          })}
                        </TagsContainer>
                      </FieldGroup>
                    )}

                    {shouldRenderRequiredField('materials') && (
                      <FieldGroup>
                        <FieldLabel>Conjuntos de tecidos e aviamento</FieldLabel>
                        <InfiniteSelect
                          placeholder='Selecione conjunto'
                          mode='readonly'
                          showSelectedBelow={false}
                          value={null}
                          disabled={!familyCollectionId}
                          fetchFunction={createLocalFetch(collectionMaterialOptions)}
                          onSelect={(option) => {
                            if (!option) return
                            addNumericSelection(setFamilyCollectionMaterialIds, option.id)
                          }}
                          getOptionLabel={(option) => option.name}
                        />
                        <TagsContainer>
                          {familyCollectionMaterialIds.map((materialId) => {
                            const label = collectionMaterialOptions.find((item) => Number(item.id) === materialId)?.name || `ID ${materialId}`
                            return (
                              <Tag key={`family-material-${materialId}`}>
                                <span>{label}</span>
                                <RemoveTagButton type='button' onClick={() => removeNumericSelection(setFamilyCollectionMaterialIds, materialId)}>×</RemoveTagButton>
                              </Tag>
                            )
                          })}
                        </TagsContainer>
                      </FieldGroup>
                    )}
                  </FormGrid>
                </Card>
              )}

              {shouldRenderRequiredField('gradeIds') && (
                <Card $inline={inline}>
                  {!inline && <CardTitle>Grades</CardTitle>}
                  {Object.keys(gradesByType).map((typeName) => (
                    <GradeTypeBlock key={`family-grade-type-${typeName}`}>
                      <GradeTypeHeader>
                        <SwitchContainer>
                          <SwitchInput
                            type='checkbox'
                            checked={enabledGradeTypes[typeName] || false}
                            onChange={(event) => toggleGradeType(typeName, event.target.checked)}
                          />
                          <SwitchTrack $checked={enabledGradeTypes[typeName] || false}>
                            <SwitchThumb $checked={enabledGradeTypes[typeName] || false} />
                          </SwitchTrack>
                        </SwitchContainer>
                        <GradeTypeLabel>{typeName}</GradeTypeLabel>
                      </GradeTypeHeader>

                      <GradeButtons>
                        {gradesByType[typeName].map((grade) => {
                          const gradeId = Number(grade.id)
                          const selected = familyGradeIds.includes(gradeId)
                          return (
                            <GradeButton
                              key={`family-grade-${gradeId}`}
                              type='button'
                              disabled={!enabledGradeTypes[typeName]}
                              $selected={selected}
                              onClick={() => toggleGradeSelection(gradeId, typeName)}
                            >
                              {grade.name}
                            </GradeButton>
                          )
                        })}
                      </GradeButtons>
                    </GradeTypeBlock>
                  ))}
                </Card>
              )}
            </>
          )}

          {!isLoadingOptions && moduleType === 'product' && (
            <>
              {(shouldRenderRequiredField('productCode') || shouldRenderRequiredField('name') || shouldRenderRequiredField('collectionId') || shouldRenderRequiredField('familyId') || shouldRenderRequiredField('groupId') || shouldRenderRequiredField('gradeIds')) && (
                <Card $inline={inline}>
                  {!inline && <CardTitle>Geral</CardTitle>}
                  <FormGrid cols={3}>
                    {shouldRenderRequiredField('productCode') && (
                      <FieldGroup>
                        <FieldLabel>Código do produto</FieldLabel>
                        <FieldInput
                          value={productCode}
                          onChange={(event) => setProductCode(event.target.value)}
                          placeholder='Digite aqui'
                        />
                      </FieldGroup>
                    )}

                    {shouldRenderRequiredField('name') && (
                      <FieldGroup>
                        <FieldLabel>Nome do produto</FieldLabel>
                        <FieldInput
                          value={productName}
                          onChange={(event) => setProductName(event.target.value)}
                          placeholder='Digite aqui'
                        />
                      </FieldGroup>
                    )}

                    {shouldRenderRequiredField('collectionId') && (
                      <FieldGroup>
                        <FieldLabel>Coleção vinculada</FieldLabel>
                        <InfiniteSelect
                          placeholder='Selecione coleção'
                          mode='readonly'
                          showSelectedBelow={false}
                          value={productCollectionId
                            ? collectionOptions.find((item) => Number(item.id) === productCollectionId) || null
                            : null}
                          fetchFunction={createLocalFetch(collectionOptions)}
                          onSelect={(option) => {
                            const nextCollectionId = option ? Number(option.id) : null
                            setProductCollectionId(nextCollectionId)
                            setProductFamilyId(null)
                            setProductGroupId(null)
                            setSelectedProductThemeId(null)
                            setSelectedProductGradeIds([])
                            setProductCompositions([{ materialId: null, usage: '', unitOfMeasurementId: null, collectionColorId: null }])
                          }}
                          getOptionLabel={(option) => option.name}
                        />
                      </FieldGroup>
                    )}

                    {shouldRenderRequiredField('familyId') && (
                      <FieldGroup>
                        <FieldLabel>Família</FieldLabel>
                        <InfiniteSelect
                          placeholder='Selecione família'
                          mode='readonly'
                          showSelectedBelow={false}
                          value={productFamilyId
                            ? familyOptions.find((item) => Number(item.id) === productFamilyId) || null
                            : null}
                          disabled={!productCollectionId}
                          fetchFunction={createLocalFetch(familyOptions)}
                          onSelect={(option) => {
                            const nextFamilyId = option ? Number(option.id) : null
                            setProductFamilyId(nextFamilyId)
                            setProductGroupId(null)
                            setSelectedProductThemeId(null)
                            setSelectedProductGradeIds([])
                          }}
                          getOptionLabel={(option) => option.name}
                        />
                      </FieldGroup>
                    )}

                    {shouldRenderRequiredField('groupId') && (
                      <FieldGroup>
                        <FieldLabel>Conjuntos</FieldLabel>
                        <InfiniteSelect
                          placeholder='Selecione conjunto'
                          mode='crud'
                          allowDelete={false}
                          showCreateOnlyIfNotExists={true}
                          showSelectedBelow={false}
                          value={productGroupId
                            ? groupOptions.find((item) => Number(item.id) === productGroupId) || null
                            : null}
                          disabled={!productFamilyId}
                          fetchFunction={createLocalFetch(groupOptions)}
                          onSelect={(option) => setProductGroupId(option ? Number(option.id) : null)}
                          onItemCreate={async (name) => {
                            const created = await createFashionGroupOption(name)
                            setProductGroupId(Number(created.id))
                            return created
                          }}
                          getOptionLabel={(option) => option.name}
                        />
                      </FieldGroup>
                    )}

                    {shouldRenderRequiredField('gradeIds') && (
                      <FieldGroup>
                        <FieldLabel>Grades</FieldLabel>
                        <InfiniteSelect
                          placeholder='Selecione grade'
                          mode='readonly'
                          showSelectedBelow={false}
                          value={null}
                          disabled={!productFamilyId}
                          fetchFunction={createLocalFetch(productGradeOptions)}
                          onSelect={(option) => {
                            if (!option) return
                            addNumericSelection(setSelectedProductGradeIds, option.id)
                          }}
                          getOptionLabel={(option) => option.name}
                        />
                        <TagsContainer>
                          {selectedProductGradeIds.map((gradeId) => {
                            const gradeLabel = productGradeOptions.find((item) => Number(item.id) === gradeId)?.name || `Grade ${gradeId}`
                            return (
                              <Tag key={`product-grade-${gradeId}`}>
                                <span>{gradeLabel}</span>
                                <RemoveTagButton type='button' onClick={() => removeNumericSelection(setSelectedProductGradeIds, gradeId)}>×</RemoveTagButton>
                              </Tag>
                            )
                          })}
                        </TagsContainer>
                      </FieldGroup>
                    )}
                  </FormGrid>
                </Card>
              )}

              {shouldRenderRequiredField('compositionItems') && (
                <Card $inline={inline}>
                  {!inline && <CardTitle>Composição</CardTitle>}
                  <CompositionTable>
                    <CompositionHeader>
                      <span>Material</span>
                      <span>Consumo</span>
                      <span>Unidade</span>
                      <span>Cor da coleção</span>
                      <span />
                    </CompositionHeader>

                    {productCompositions.map((item, index) => (
                      <CompositionRow key={`product-composition-${index}`}>
                        <InfiniteSelect
                          placeholder='Selecione material'
                          mode='crud'
                          allowDelete={false}
                          showCreateOnlyIfNotExists={true}
                          value={item.materialId
                            ? materialOptions.find((option) => Number(option.id) === item.materialId) || null
                            : null}
                          showSelectedBelow={false}
                          fetchFunction={createLocalFetch(materialOptions)}
                          onSelect={(option) => upsertProductComposition(index, { materialId: option ? Number(option.id) : null })}
                          onItemCreate={async (name) => {
                            const created = await createFashionMaterialOption(name)
                            upsertProductComposition(index, { materialId: Number(created.id) })
                            return created
                          }}
                          getOptionLabel={(option) => option.name}
                        />

                        <FieldInput
                          type='number'
                          min='0'
                          step='0.01'
                          value={item.usage}
                          onChange={(event) => upsertProductComposition(index, { usage: event.target.value })}
                          placeholder='0'
                        />

                        <FieldSelect
                          value={item.unitOfMeasurementId || ''}
                          onChange={(event) => upsertProductComposition(index, { unitOfMeasurementId: Number(event.target.value) || null })}
                        >
                          <option value=''>Selecione</option>
                          {unitOptions.map((unit) => (
                            <option key={`unit-${String(unit.id)}`} value={String(unit.id)}>{unit.name}</option>
                          ))}
                        </FieldSelect>

                        <FieldSelect
                          value={item.collectionColorId || ''}
                          onChange={(event) => upsertProductComposition(index, { collectionColorId: Number(event.target.value) || null })}
                          disabled={!productCollectionId}
                        >
                          <option value=''>Selecione</option>
                          {collectionColorOptions.map((color) => (
                            <option key={`product-color-${String(color.id)}`} value={String(color.id)}>{color.name}</option>
                          ))}
                        </FieldSelect>

                        <InlineDeleteButton type='button' onClick={() => removeProductComposition(index)}>×</InlineDeleteButton>
                      </CompositionRow>
                    ))}
                  </CompositionTable>

                  <AddItemButton type='button' onClick={addProductComposition}>+ Adicionar item</AddItemButton>
                </Card>
              )}
            </>
          )}

          {!isLoadingOptions && moduleType === 'fabric' && (
            <Card $inline={inline}>
              {!inline && <CardTitle>Cadastro de tecido</CardTitle>}
              <FormGrid cols={2}>
                {shouldRenderRequiredField('code') && (
                  <FieldGroup>
                    <FieldLabel>Código</FieldLabel>
                    <FieldInput value={fabricCode} onChange={(event) => setFabricCode(event.target.value)} placeholder='Digite aqui' />
                  </FieldGroup>
                )}

                {shouldRenderRequiredField('material') && (
                  <FieldGroup>
                    <FieldLabel>Material</FieldLabel>
                    <InfiniteSelect
                      placeholder='Procure aqui'
                      mode='crud'
                      allowDelete={false}
                      showCreateOnlyIfNotExists={true}
                      showSelectedBelow={false}
                      value={fabricMaterialId
                        ? materialOptions.find((item) => Number(item.id) === fabricMaterialId) || null
                        : null}
                      fetchFunction={createLocalFetch(materialOptions)}
                      onSelect={(option) => setFabricMaterialId(option ? Number(option.id) : null)}
                      onItemCreate={async (name) => {
                        const created = await createStockMaterialOption(name)
                        setFabricMaterialId(Number(created.id))
                        return created
                      }}
                      getOptionLabel={(option) => option.name}
                    />
                  </FieldGroup>
                )}

                {shouldRenderRequiredField('color') && (
                  <FieldGroup>
                    <FieldLabel>Cor</FieldLabel>
                    <InfiniteSelect
                      name='integrated-fabric-item-color'
                      placeholder='Selecione cor'
                      mode='crud'
                      allowDelete={false}
                      showCreateOnlyIfNotExists={true}
                      showSelectedBelow={false}
                      value={fabricItemColorId
                        ? fabricColorOptions.find((item) => Number(item.id) === fabricItemColorId) || null
                        : null}
                      fetchFunction={createLocalFetch(fabricColorOptions)}
                      onSelect={(option) => {
                        if (!option) {
                          setFabricItemColorId(null)
                          setFabricColor('')
                          setFabricPantone(null)
                          return
                        }
                        const nextColorName = String(option.name || '').trim()
                        const nextColorId = Number(option.id)
                        const selectedColorOption = fabricColorOptions.find((item) => String(item.id) === String(option.id))
                        const nextPantone = selectedColorOption?.pantone || null
                        setFabricItemColorId(Number.isFinite(nextColorId) ? nextColorId : null)
                        setFabricColor(nextColorName)
                        setFabricPantone(nextPantone)
                      }}
                      onItemCreate={async (name) => {
                        const created = await createStockColorOption(name)
                        const createdColorId = Number(created.id)
                        setFabricItemColorId(Number.isFinite(createdColorId) ? createdColorId : null)
                        setFabricColor(String(created.name || '').trim())
                        setFabricPantone(created.pantone || null)
                        return created
                      }}
                      getOptionLabel={(option) => option.name}
                    />
                  </FieldGroup>
                )}

                {shouldRenderRequiredField('pantoneCode') && (
                  <FieldGroup>
                    <FieldLabel>Pantone</FieldLabel>
                    <PantoneSelect
                      value={fabricPantone}
                      onSelect={(pantone) => setFabricPantone(pantone)}
                    />
                  </FieldGroup>
                )}
              </FormGrid>
            </Card>
          )}

          {!isLoadingOptions && moduleType === 'trim' && (shouldRenderRequiredField('name') || shouldRenderRequiredField('categoryId') || shouldRenderRequiredField('materialId') || shouldRenderRequiredField('colors')) && (
            <Card $inline={inline}>
              {!inline && <CardTitle>Cadastro de aviamento</CardTitle>}
              <FormGrid cols={2}>
                {shouldRenderRequiredField('name') && (
                  <FieldGroup>
                    <FieldLabel>Nome</FieldLabel>
                    <FieldInput value={trimName} onChange={(event) => setTrimName(event.target.value)} placeholder='Digite aqui' />
                  </FieldGroup>
                )}

                {shouldRenderRequiredField('categoryId') && (
                  <FieldGroup>
                    <FieldLabel>Categoria</FieldLabel>
                    <InfiniteSelect
                      placeholder='Buscar ou criar categoria'
                      mode='crud'
                      allowDelete={false}
                      showCreateOnlyIfNotExists={true}
                      showSelectedBelow={false}
                      value={trimCategoryId
                        ? resourceCategoryOptions.find((item) => Number(item.id) === trimCategoryId) || null
                        : null}
                      fetchFunction={createLocalFetch(resourceCategoryOptions)}
                      onSelect={(option) => setTrimCategoryId(option ? Number(option.id) : null)}
                      onItemCreate={async (name) => {
                        const created = await createResourceCategoryOption(name)
                        setTrimCategoryId(Number(created.id))
                        return created
                      }}
                      getOptionLabel={(option) => option.name}
                    />
                  </FieldGroup>
                )}

                {shouldRenderRequiredField('materialId') && (
                  <FieldGroup>
                    <FieldLabel>Material</FieldLabel>
                    <InfiniteSelect
                      placeholder='Buscar material'
                      mode='crud'
                      allowDelete={false}
                      showCreateOnlyIfNotExists={true}
                      showSelectedBelow={false}
                      value={trimMaterialId
                        ? materialOptions.find((item) => Number(item.id) === trimMaterialId) || null
                        : null}
                      fetchFunction={createLocalFetch(materialOptions)}
                      onSelect={(option) => setTrimMaterialId(option ? Number(option.id) : null)}
                      onItemCreate={async (name) => {
                        const created = await createStockMaterialOption(name)
                        setTrimMaterialId(Number(created.id))
                        return created
                      }}
                      getOptionLabel={(option) => option.name}
                    />
                  </FieldGroup>
                )}

                {shouldRenderRequiredField('colors') && (
                  <FieldGroup>
                    <FieldLabel>Cor</FieldLabel>
                    <InfiniteSelect
                      name='integrated-trim-item-color'
                      placeholder='Selecione cor'
                      mode='crud'
                      allowDelete={false}
                      showCreateOnlyIfNotExists={true}
                      showSelectedBelow={false}
                      value={trimItemColorId
                        ? trimColorOptions.find((item) => Number(item.id) === trimItemColorId) || null
                        : null}
                      fetchFunction={createLocalFetch(trimColorOptions)}
                      onSelect={(option) => {
                        if (!option) {
                          setTrimItemColorId(null)
                          setTrimColor('')
                          setTrimPantone(null)
                          return
                        }
                        const nextColorName = String(option.name || '').trim()
                        const nextColorId = Number(option.id)
                        const selectedColorOption = trimColorOptions.find((item) => String(item.id) === String(option.id))
                        const nextPantone = selectedColorOption?.pantone || null
                        setTrimItemColorId(Number.isFinite(nextColorId) ? nextColorId : null)
                        setTrimColor(nextColorName)
                        setTrimPantone(nextPantone)
                      }}
                      onItemCreate={async (name) => {
                        const created = await createStockColorOption(name)
                        const createdColorId = Number(created.id)
                        setTrimItemColorId(Number.isFinite(createdColorId) ? createdColorId : null)
                        setTrimColor(String(created.name || '').trim())
                        setTrimPantone(created.pantone || null)
                        return created
                      }}
                      getOptionLabel={(option) => option.name}
                    />
                  </FieldGroup>
                )}

                {shouldRenderRequiredField('colors') && (
                  <FieldGroup>
                    <FieldLabel>Pantone</FieldLabel>
                    <PantoneSelect
                      value={trimPantone}
                      onSelect={(pantone) => setTrimPantone(pantone)}
                    />
                  </FieldGroup>
                )}
              </FormGrid>
            </Card>
          )}

          {!isLoadingOptions && optionalFieldConfigsByGrid.length > 0 && (
            <Card $inline={inline}>
              <CardTitle>Campos opcionais selecionados</CardTitle>
              <FormGrid cols={2}>
                {optionalFieldConfigsByGrid.map((fieldConfig) => (
                  <FieldGroup key={`optional-registration-field-${fieldConfig.field}`}>
                    <FieldLabel>{fieldConfig.fieldName}</FieldLabel>

                    {fieldConfig.rendererType === 'collection-groups' ? (
                      <>
                        <InfiniteSelect
                          placeholder='Selecione ou crie grupo'
                          mode='crud'
                          allowDelete={false}
                          showCreateOnlyIfNotExists={true}
                          showSelectedBelow={false}
                          value={null}
                          fetchFunction={createLocalFetch(groupOptions)}
                          onSelect={(option) => {
                            if (!option) return
                            addNumericSelection(setSelectedCollectionGroupIds, option.id)
                          }}
                          onItemCreate={async (name) => {
                            const created = await createFashionGroupOption(name)
                            addNumericSelection(setSelectedCollectionGroupIds, created.id)
                            return created
                          }}
                          getOptionLabel={(option) => option.name}
                        />
                        <TagsContainer>
                          {selectedCollectionGroupIds.map((groupId) => {
                            const groupLabel = groupOptions.find((item) => Number(item.id) === groupId)?.name || `Grupo ${groupId}`
                            return (
                              <Tag key={`collection-optional-group-${groupId}`}>
                                <span>{groupLabel}</span>
                                <RemoveTagButton type='button' onClick={() => removeNumericSelection(setSelectedCollectionGroupIds, groupId)}>×</RemoveTagButton>
                              </Tag>
                            )
                          })}
                        </TagsContainer>
                      </>
                    ) : fieldConfig.rendererType === 'collection-themes' ? (
                      <>
                        <InfiniteSelect
                          placeholder='Selecione ou crie tema'
                          mode='crud'
                          allowDelete={false}
                          showCreateOnlyIfNotExists={true}
                          showSelectedBelow={false}
                          value={null}
                          fetchFunction={createLocalFetch(collectionThemeOptions)}
                          onSelect={(option) => {
                            if (!option) return
                            addNumericSelection(setSelectedCollectionThemeIds, option.id)
                          }}
                          onItemCreate={async (name) => {
                            const created = await createFashionThemeOption(name)
                            addNumericSelection(setSelectedCollectionThemeIds, created.id)
                            return created
                          }}
                          getOptionLabel={(option) => option.name}
                        />
                        <TagsContainer>
                          {selectedCollectionThemeIds.map((themeId) => {
                            const themeLabel = collectionThemeOptions.find((item) => Number(item.id) === themeId)?.name || `Tema ${themeId}`
                            return (
                              <Tag key={`collection-optional-theme-${themeId}`}>
                                <span>{themeLabel}</span>
                                <RemoveTagButton type='button' onClick={() => removeNumericSelection(setSelectedCollectionThemeIds, themeId)}>×</RemoveTagButton>
                              </Tag>
                            )
                          })}
                        </TagsContainer>
                      </>
                    ) : fieldConfig.rendererType === 'family-groups' ? (
                      <>
                        <InfiniteSelect
                          placeholder='Selecione grupo'
                          mode='readonly'
                          showSelectedBelow={false}
                          value={null}
                          fetchFunction={createLocalFetch(familyGroupOptions)}
                          onSelect={(option) => {
                            if (!option) return
                            addNumericSelection(setSelectedFamilyGroupIds, option.id)
                          }}
                          getOptionLabel={(option) => option.name}
                        />
                        <TagsContainer>
                          {selectedFamilyGroupIds.map((groupId) => {
                            const groupLabel = familyGroupOptions.find((item) => Number(item.id) === groupId)?.name || `Grupo ${groupId}`
                            return (
                              <Tag key={`family-optional-group-${groupId}`}>
                                <span>{groupLabel}</span>
                                <RemoveTagButton type='button' onClick={() => removeNumericSelection(setSelectedFamilyGroupIds, groupId)}>×</RemoveTagButton>
                              </Tag>
                            )
                          })}
                        </TagsContainer>
                      </>
                    ) : fieldConfig.rendererType === 'family-themes' ? (
                      <>
                        <InfiniteSelect
                          placeholder='Selecione tema'
                          mode='readonly'
                          showSelectedBelow={false}
                          value={null}
                          fetchFunction={createLocalFetch(familyThemeOptions)}
                          onSelect={(option) => {
                            if (!option) return
                            addNumericSelection(setSelectedFamilyThemeIds, option.id)
                          }}
                          getOptionLabel={(option) => option.name}
                        />
                        <TagsContainer>
                          {selectedFamilyThemeIds.map((themeId) => {
                            const themeLabel = familyThemeOptions.find((item) => Number(item.id) === themeId)?.name || `Tema ${themeId}`
                            return (
                              <Tag key={`family-optional-theme-${themeId}`}>
                                <span>{themeLabel}</span>
                                <RemoveTagButton type='button' onClick={() => removeNumericSelection(setSelectedFamilyThemeIds, themeId)}>×</RemoveTagButton>
                              </Tag>
                            )
                          })}
                        </TagsContainer>
                      </>
                    ) : fieldConfig.rendererType === 'product-theme' ? (
                      <InfiniteSelect
                        placeholder='Selecione tema'
                        mode='readonly'
                        showSelectedBelow={false}
                        value={selectedProductThemeId
                          ? productThemeOptions.find((item) => Number(item.id) === selectedProductThemeId) || null
                          : null}
                        fetchFunction={createLocalFetch(productThemeOptions)}
                        onSelect={(option) => {
                          setSelectedProductThemeId(option ? Number(option.id) : null)
                        }}
                        getOptionLabel={(option) => option.name}
                      />
                    ) : fieldConfig.rendererType === 'fabric-measurement-unit' ? (
                      <InfiniteSelect
                        placeholder='Selecione unidade'
                        mode='readonly'
                        showSelectedBelow={false}
                        value={
                          optionalFieldValues[fieldConfig.field]
                            ? measurementUnitChoices.find((option) => String(option.id) === String(optionalFieldValues[fieldConfig.field])) || null
                            : null
                        }
                        fetchFunction={createLocalFetch(measurementUnitChoices)}
                        onSelect={(option) => {
                          const nextValue = option ? String(option.id) : ''
                          setOptionalFieldValues((prev) => ({ ...prev, [fieldConfig.field]: nextValue }))
                        }}
                        getOptionLabel={(option) => option.name}
                      />
                    ) : fieldConfig.rendererType === 'trim-stock-unit' ? (
                      <InfiniteSelect
                        placeholder='Selecione unidade de estoque'
                        mode='readonly'
                        showSelectedBelow={false}
                        value={
                          optionalFieldValues[fieldConfig.field]
                            ? measurementUnitChoices.find((option) => String(option.id) === String(optionalFieldValues[fieldConfig.field])) || null
                            : null
                        }
                        fetchFunction={createLocalFetch(measurementUnitChoices)}
                        onSelect={(option) => {
                          const nextValue = option ? String(option.id) : ''
                          setOptionalFieldValues((prev) => ({ ...prev, [fieldConfig.field]: nextValue }))
                        }}
                        getOptionLabel={(option) => option.name}
                      />
                    ) : fieldConfig.rendererType === 'trim-type' ? (
                      <InfiniteSelect
                        placeholder='Selecione tipo'
                        mode='readonly'
                        showSelectedBelow={false}
                        disabled={!trimCategoryId}
                        value={
                          optionalFieldValues[fieldConfig.field]
                            ? trimTypeOptions.find((option) => Number(option.id) === Number(optionalFieldValues[fieldConfig.field])) || null
                            : null
                        }
                        fetchFunction={createLocalFetch(trimTypeOptions)}
                        onSelect={(option) => {
                          const nextValue = option ? String(option.id) : ''
                          setOptionalFieldValues((prev) => ({ ...prev, [fieldConfig.field]: nextValue }))
                        }}
                        getOptionLabel={(option) => option.name}
                      />
                    ) : fieldConfig.rendererType === 'trim-stock-street' ? (
                      <InfiniteSelect
                        placeholder='Selecione rua do estoque'
                        mode='readonly'
                        showSelectedBelow={false}
                        value={
                          optionalFieldValues[fieldConfig.field]
                            ? trimStockStreetOptions.find((option) => Number(option.id) === Number(optionalFieldValues[fieldConfig.field])) || null
                            : null
                        }
                        fetchFunction={createLocalFetch(trimStockStreetOptions)}
                        onSelect={(option) => {
                          const nextValue = option ? String(option.id) : ''
                          setOptionalFieldValues((prev) => ({ ...prev, [fieldConfig.field]: nextValue }))
                        }}
                        getOptionLabel={(option) => option.name}
                      />
                    ) : fieldConfig.rendererType === 'trim-suppliers' ? (
                      <>
                        <InfiniteSelect
                          placeholder='Selecione fornecedor'
                          mode='readonly'
                          showSelectedBelow={false}
                          value={null}
                          fetchFunction={createLocalFetch(trimSupplierOptions)}
                          onSelect={(option) => {
                            if (!option) return
                            addNumericSelection(setSelectedTrimSupplierIds, option.id)
                          }}
                          getOptionLabel={(option) => option.name}
                        />
                        <TagsContainer>
                          {selectedTrimSupplierIds.map((supplierId) => {
                            const supplierLabel = trimSupplierOptions.find((item) => Number(item.id) === supplierId)?.name || `Fornecedor ${supplierId}`
                            return (
                              <Tag key={`trim-optional-supplier-${supplierId}`}>
                                <span>{supplierLabel}</span>
                                <RemoveTagButton type='button' onClick={() => removeNumericSelection(setSelectedTrimSupplierIds, supplierId)}>×</RemoveTagButton>
                              </Tag>
                            )
                          })}
                        </TagsContainer>
                      </>
                    ) : fieldConfig.rendererType === 'trim-prices' ? (
                      <FormGrid cols={2}>
                        <FieldGroup>
                          <FieldLabel>Valor</FieldLabel>
                          <FieldInput
                            type='number'
                            step='0.01'
                            min='0'
                            value={trimPriceValue}
                            onChange={(event) => setTrimPriceValue(event.target.value)}
                            placeholder='0,00'
                          />
                        </FieldGroup>
                        <FieldGroup>
                          <FieldLabel>Unidade</FieldLabel>
                          <InfiniteSelect
                            placeholder='Selecione unidade'
                            mode='readonly'
                            showSelectedBelow={false}
                            value={trimPriceUnitId
                              ? trimPriceUnitOptions.find((option) => Number(option.id) === trimPriceUnitId) || null
                              : null}
                            fetchFunction={createLocalFetch(trimPriceUnitOptions)}
                            onSelect={(option) => {
                              setTrimPriceUnitId(option ? Number(option.id) : null)
                            }}
                            getOptionLabel={(option) => option.name}
                          />
                        </FieldGroup>
                      </FormGrid>
                    ) : fieldConfig.valueType === 'boolean' ? (
                      <FieldSelect
                        value={optionalFieldValues[fieldConfig.field] || ''}
                        onChange={(event) => {
                          const nextValue = event.target.value
                          setOptionalFieldValues((prev) => ({ ...prev, [fieldConfig.field]: nextValue }))
                        }}
                      >
                        <option value=''>Selecione</option>
                        <option value='true'>Sim</option>
                        <option value='false'>Não</option>
                      </FieldSelect>
                    ) : (
                      <FieldInput
                        type={fieldConfig.valueType === 'number' ? 'number' : 'text'}
                        step={fieldConfig.valueType === 'number' ? '0.01' : undefined}
                        value={optionalFieldValues[fieldConfig.field] || ''}
                        onChange={(event) => {
                          const nextValue = event.target.value
                          setOptionalFieldValues((prev) => ({ ...prev, [fieldConfig.field]: nextValue }))
                        }}
                        placeholder='Digite aqui'
                      />
                    )}
                  </FieldGroup>
                ))}
              </FormGrid>
            </Card>
          )}
        </Body>

        <Footer $inline={inline}>
          {!inline && <CancelButton type='button' onClick={onClose}>Cancelar</CancelButton>}
          {inline && validationMessage && !isLoadingOptions && (
            <InlineValidationText>{validationMessage}</InlineValidationText>
          )}
          {inline && autoCreateWhenReady && !isLoadingOptions && (
            autoCreateCreatedRecord ? (
              <InlineSuccessText>
                Registro criado com sucesso.
                {autoCreateCreatedRecord.viewUrl && (
                  <InlineSuccessLink href={autoCreateCreatedRecord.viewUrl} target='_blank' rel='noreferrer'>
                    Ver item
                  </InlineSuccessLink>
                )}
              </InlineSuccessText>
            ) : (
              <InlineValidationText>
                Registro automático: após preencher os campos necessários, salve o card para concluir o registro.
              </InlineValidationText>
            )
          )}
          {!autoCreateWhenReady && (
            <SaveButton type='button' onClick={handleSubmit} disabled={createButtonDisabled}>
              {isSubmitting ? 'Criando...' : 'Criar registro'}
            </SaveButton>
          )}
        </Footer>
      </Container>
    </Overlay>
  )
}

const Overlay = styled.div<{ $inline?: boolean }>`
  position: ${(props) => props.$inline ? 'static' : 'fixed'};
  inset: ${(props) => props.$inline ? 'auto' : '0'};
  background-color: ${(props) => props.$inline ? 'transparent' : 'rgba(0, 0, 0, 0.5)'};
  display: flex;
  align-items: ${(props) => props.$inline ? 'stretch' : 'center'};
  justify-content: ${(props) => props.$inline ? 'stretch' : 'center'};
  z-index: ${(props) => props.$inline ? '1' : '12000'};
`

const Container = styled.div<{ $inline?: boolean }>`
  width: ${(props) => props.$inline ? '100%' : 'min(1000px, 95vw)'};
  max-height: ${(props) => props.$inline ? 'none' : '90vh'};
  background-color: #fff;
  border-radius: ${(props) => props.$inline ? '10px' : '12px'};
  display: flex;
  flex-direction: column;
  box-shadow: ${(props) => props.$inline ? '0 8px 24px rgba(0, 0, 0, 0.08)' : '0 20px 60px rgba(0, 0, 0, 0.3)'};
  border: ${(props) => props.$inline ? '1px solid #e8edf2' : 'none'};
`

const Header = styled.div<{ $inline?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) => props.$inline ? '0.85rem 1rem' : '1rem 1.2rem'};
  border-bottom: 1px solid #ececec;
  background-color: ${(props) => props.$inline ? '#f7fafc' : '#fff'};
`

const Title = styled.h3<{ $inline?: boolean }>`
  margin: 0;
  font-size: ${(props) => props.$inline ? '1.15rem' : '1.3rem'};
  font-weight: 700;
  color: #303030;
`

const CloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: #303030;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const Body = styled.div<{ $inline?: boolean }>`
  padding: ${(props) => props.$inline ? '0.85rem 1rem' : '1rem 1.2rem'};
  overflow: ${(props) => props.$inline ? 'visible' : 'auto'};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: ${(props) => props.$inline ? '#ffffff' : '#f4f4f4'};
`

const Card = styled.div<{ $inline?: boolean }>`
  background-color: ${(props) => props.$inline ? '#f8fafc' : '#fff'};
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  border: ${(props) => props.$inline ? '1px solid #e6edf3' : 'none'};
`

const CardTitle = styled.h4`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #303030;
`

const FormGrid = styled.div<{ cols: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.cols}, minmax(0, 1fr));
  gap: 0.9rem 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`

const FieldLabel = styled.label`
  font-size: 14px;
  font-weight: 700;
  color: #303030;
`

const FieldInput = styled.input`
  width: 100%;
  height: 40px;
  border-radius: 100px;
  border: 1px solid #ebebeb;
  background-color: #ebebeb;
  color: #696969;
  padding: 0 0.85rem;
  box-sizing: border-box;
  outline: none;

  &::placeholder {
    color: #c4c4c4;
  }
`

const FieldSelect = styled.select`
  width: 100%;
  height: 40px;
  border-radius: 100px;
  border: 1px solid #ebebeb;
  background-color: #ebebeb;
  color: #696969;
  padding: 0 0.85rem;
  box-sizing: border-box;
  outline: none;
`

const ErrorBox = styled.div`
  background-color: #fff2f2;
  color: #b42318;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 0.6rem 0.8rem;
  font-size: 0.9rem;
`

const LoadingText = styled.p`
  margin: 0;
  color: #696969;
`

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`

const Tag = styled.div<{ $clickable?: boolean; $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.6rem;
  border-radius: 100px;
  border: 1px solid ${(props) => props.$active ? '#0090d3' : '#dfdfdf'};
  background-color: ${(props) => props.$active ? '#e6f7ff' : '#fff'};
  color: #555;
  font-size: 13px;
  cursor: ${(props) => props.$clickable ? 'pointer' : 'default'};
`

const RemoveTagButton = styled.button`
  border: none;
  background: transparent;
  color: #777;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  padding: 0;
`

const TagHint = styled.span`
  color: #777;
  font-size: 11px;
`

const TagError = styled.span`
  color: #d92d20;
  font-size: 11px;
`

const Swatch = styled.span<{ color: string }>`
  width: 11px;
  height: 11px;
  border-radius: 2px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  background-color: ${(props) => props.color?.startsWith('#') ? props.color : `#${props.color}`};
  display: inline-block;
`

const PantoneContainer = styled.div`
  margin-top: 0.4rem;
  padding: 0.65rem;
  border-radius: 8px;
  border: 1px solid #bfdbfe;
  background-color: #f0f7ff;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`

const GradeTypeBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`

const GradeTypeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
`

const GradeTypeLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`

const SwitchContainer = styled.label`
  position: relative;
  display: inline-flex;
  align-items: center;
`

const SwitchInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`

const SwitchTrack = styled.span<{ $checked: boolean }>`
  width: 44px;
  height: 26px;
  border-radius: 30px;
  background-color: ${(props) => props.$checked ? '#0090d3' : '#bcbcbc'};
  display: inline-flex;
  align-items: center;
  padding: 2px;
  box-sizing: border-box;
`

const SwitchThumb = styled.span<{ $checked: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: #fff;
  transform: translateX(${(props) => props.$checked ? '18px' : '0'});
  transition: transform 0.2s ease;
`

const GradeButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const GradeButton = styled.button<{ $selected: boolean }>`
  padding: 0.45rem 0.85rem;
  border-radius: 100px;
  border: 1px solid ${(props) => props.$selected ? '#0090d3' : '#e0e0e0'};
  background-color: ${(props) => props.$selected ? '#e6f7ff' : '#f7f7f7'};
  color: ${(props) => props.$selected ? '#0090d3' : '#666'};
  font-size: 13px;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const CompositionTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`

const CompositionHeader = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr auto;
  gap: 0.55rem;
  font-size: 13px;
  font-weight: 700;
  color: #303030;

  @media (max-width: 900px) {
    display: none;
  }
`

const CompositionRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr auto;
  gap: 0.55rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 0.75rem;
    border: 1px solid #ececec;
    border-radius: 10px;
  }
`

const InlineDeleteButton = styled.button`
  border: none;
  background: #f5f5f5;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  cursor: pointer;
  color: #555;
  font-size: 18px;
  line-height: 1;
`

const AddItemButton = styled.button`
  margin-top: 0.35rem;
  width: fit-content;
  border: 1px solid #0090d3;
  color: #0090d3;
  background: #fff;
  border-radius: 100px;
  padding: 0.55rem 0.95rem;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
`

const DraftSessionCard = styled.div`
  border: 1px solid #e8edf2;
  background: #f8fbfd;
  border-radius: 10px;
  padding: 0.8rem;
  margin-bottom: 0.85rem;
`

const InlineActionButton = styled.button`
  background-color: #fff;
  color: #0090d3;
  border: 1px solid #0090d3;
  border-radius: 100px;
  padding: 0.65rem 0.95rem;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
`

const Footer = styled.div<{ $inline?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.7rem;
  padding: ${(props) => props.$inline ? '0.8rem 1rem 1rem 1rem' : '1rem 1.2rem'};
  border-top: 1px solid #ececec;
`

const InlineValidationText = styled.span`
  color: #9f1239;
  font-size: 12px;
  font-weight: 600;
`

const InlineSuccessText = styled.span`
  color: #166534;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
`

const InlineSuccessLink = styled.a`
  color: #166534;
  text-decoration: underline;
  font-weight: 700;

  &:hover {
    color: #14532d;
  }
`

const CancelButton = styled.button`
  background-color: #fff;
  color: #0090d3;
  border: 1px solid #0090d3;
  border-radius: 100px;
  padding: 0.65rem 1rem;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
`

const SaveButton = styled.button`
  background-color: #0090d3;
  color: #fff;
  border: none;
  border-radius: 100px;
  padding: 0.65rem 1rem;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export default IntegratedWriteCreateModal
